/**
 * Connectors Settings Page
 * 
 * Manage external integrations including:
 * - Miro: Import sticky notes as ideas
 * - Jira: Export priorities as epics
 */

import { useState, useEffect, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { INTEGRATIONS_ENGINE_URL } from '../../../lib/engineUrls';
import { getIntegrationsApi, type SlackChannel } from '../../../lib/integrationsApi';
import {
  Link2,
  Plus,
  Settings,
  Trash2,
  RefreshCcw,
  Check,
  AlertCircle,

  ArrowDownToLine,
  ArrowUpFromLine,
  Loader2,
  Link2 as Link2Icon,
} from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { getCurrentWorkspaceId } from '../../../lib/supabaseSync';
import {
  CONNECTOR_REGISTRY,
  type ConnectorType,
  fetchConnectorConfigsFromDB,
  saveConnectorConfigToDB,
  type ConnectorConfigDB,
} from '../../../lib/connectors';
import {
  listConnectorConfigs,
  enableWorkspaceConnector,
  disableConnector,
} from '../../../lib/connectorConfigsService';
import {
  type AIProvider,
  aiKeyPreview,
  hasSavedAiKey,
  removeMyAiKey,
  saveMyAiKey,
  useMyAiKeys,
  resolveAIProvider,
  setAIProvider,
  providerHasApiKey,
  getAIProvider,
} from '../../../lib/aiProviderKeys';
import { ConnectorIcons } from '../../../lib/connectorIcons';

// Re-export for settings pages that import from Connectors.
export type { AIProvider };
// eslint-disable-next-line react-refresh/only-export-components -- shared with sibling settings pages; splitting into its own module is a separate refactor
export { getAIProvider, setAIProvider };

const ChatGPTLogo = () => (
  <svg viewBox="0 0 41 41" className="w-8 h-8" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M37.532 16.87a9.963 9.963 0 0 0-.856-8.184 10.078 10.078 0 0 0-10.855-4.835 9.964 9.964 0 0 0-6.13-3.636 10.079 10.079 0 0 0-11.784 4.958 9.962 9.962 0 0 0-6.68 4.836 10.079 10.079 0 0 0 1.24 11.817 9.965 9.965 0 0 0 .856 8.185 10.079 10.079 0 0 0 10.855 4.835 9.965 9.965 0 0 0 6.129 3.636 10.079 10.079 0 0 0 11.785-4.959 9.965 9.965 0 0 0 6.679-4.835 10.079 10.079 0 0 0-1.239-11.818zm-17.081 23.362a7.474 7.474 0 0 1-4.801-1.735c.061-.033.168-.091.237-.134l7.964-4.6a1.294 1.294 0 0 0 .655-1.134V19.054l3.366 1.944a.12.12 0 0 1 .066.092v9.299a7.505 7.505 0 0 1-7.487 7.843zm-16.124-6.874a7.474 7.474 0 0 1-.894-5.023c.06.036.162.099.237.141l7.964 4.6a1.297 1.297 0 0 0 1.308 0l9.724-5.614v3.888a.12.12 0 0 1-.048.103l-8.051 4.649a7.504 7.504 0 0 1-10.24-2.744zm-2.09-17.496a7.474 7.474 0 0 1 3.908-3.285c0 .068-.004.19-.004.274v9.201a1.294 1.294 0 0 0 .654 1.132l9.723 5.614-3.366 1.944a.12.12 0 0 1-.114.012L4.502 23.252a7.504 7.504 0 0 1-.265-7.39zm27.688 6.44-9.724-5.615 3.367-1.943a.121.121 0 0 1 .114-.012l8.048 4.648a7.498 7.498 0 0 1-1.158 13.528v-9.476a1.293 1.293 0 0 0-.647-1.13zm3.35-5.043c-.059-.037-.162-.099-.236-.141l-7.965-4.6a1.298 1.298 0 0 0-1.308 0l-9.723 5.614v-3.888a.12.12 0 0 1 .048-.103l8.05-4.645a7.497 7.497 0 0 1 11.135 7.763zm-21.063 6.929-3.367-1.944a.12.12 0 0 1-.065-.092v-9.299a7.497 7.497 0 0 1 12.293-5.756 6.94 6.94 0 0 0-.236.134l-7.965 4.6a1.294 1.294 0 0 0-.654 1.132l-.006 11.225zm1.829-3.943 4.33-2.501 4.332 2.497v4.995l-4.331 2.5-4.331-2.5V20.265z"
      fill="currentColor"
    />
  </svg>
);

const ClaudeLogo = () => (
  <svg viewBox="0 0 48 48" className="w-8 h-8" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M28.327 8L14 40h5.91l2.964-7.726h13.24L39.077 40H45L30.673 8h-2.346zm-3.644 19.337 4.817-12.566 4.817 12.566h-9.634z"
      fill="currentColor"
    />
  </svg>
);

// ============================================================================
// Types
// ============================================================================

interface ConnectorConnection {
  id: string;
  type: ConnectorType;
  name: string;
  status: 'connected' | 'disconnected' | 'error';
  lastSync?: string;
  config?: Record<string, unknown>;
}


// ============================================================================
// DB Storage helpers
// Maps ConnectorConfigDB rows to the local ConnectorConnection shape and back.
// §2.1 compliance: connector state lives in connector_configs (Supabase).
// auth_data stores credential references — raw tokens are stored here until
// a secrets vault is wired in Phase 2 (acceptable; RLS-protected JSONB column).
// ============================================================================

function dbRowToConnection(row: ConnectorConfigDB): ConnectorConnection {
  return {
    id: row.id,
    type: row.connectorType,
    name: row.displayName ?? row.connectorType,
    status: row.isActive ? 'connected' : 'disconnected',
    lastSync: row.lastSyncedAt ?? undefined,
    config: row.authData as Record<string, unknown>,
  };
}

async function loadConnectionsFromDB(workspaceId: string): Promise<ConnectorConnection[]> {
  const { data, error } = await fetchConnectorConfigsFromDB(workspaceId);
  if (error) console.warn('[Connectors] failed to load from DB:', error);
  // Only treat rows that have actual credentials as "connected".
  // Rows with is_active=true but empty auth_data are "available" (enabled for workspace
  // but not yet authenticated) — those are tracked separately via connectorEnabled state.
  return (data ?? [])
    .filter(r => r.isActive && Object.keys(r.authData ?? {}).length > 0)
    .map(dbRowToConnection);
}

async function persistConnection(
  workspaceId: string,
  connectorType: ConnectorType,
  displayName: string,
  authData: Record<string, unknown>,
): Promise<{ id: string } | null> {
  const { data, error } = await saveConnectorConfigToDB(workspaceId, connectorType, {
    displayName,
    authData,
    isActive: true,
  });
  if (error) {
    console.error('[Connectors] failed to save to DB:', error);
    return null;
  }
  return data ? { id: data.id } : null;
}

async function removeConnection(workspaceId: string, connectorType: ConnectorType): Promise<void> {
  // Soft-remove: mark inactive so history is preserved
  await saveConnectorConfigToDB(workspaceId, connectorType, { isActive: false });
}

// ============================================================================
// Component
// ============================================================================

export default function Connectors() {
  // AI Provider state
  const [selectedAIProvider, setSelectedAIProvider] = useState<AIProvider | null>(
    () => resolveAIProvider(),
  );
  const [aiProviderSaved, setAIProviderSaved] = useState(false);

  // AI provider key management (connection ≠ active provider)
  const [keyModalProvider, setKeyModalProvider] = useState<AIProvider | null>(null);
  const [keyInput, setKeyInput] = useState('');
  const [keySaved, setKeySaved] = useState(false);

  const [keyError, setKeyError] = useState<string | null>(null);
  // A person's own keys are kept on the server (migration 982); re-render when
  // they are saved, removed or loaded.
  useMyAiKeys();
  const hasOpenAIKey = providerHasApiKey('openai');
  const hasAnthropicKey = providerHasApiKey('anthropic');
  const openaiKeyPreview = aiKeyPreview('openai');
  const anthropicKeyPreview = aiKeyPreview('anthropic');

  function openKeyModal(provider: AIProvider) {
    setKeyModalProvider(provider);
    setKeyInput('');
    setKeyError(null);
  }

  function closeKeyModal() {
    setKeyModalProvider(null);
    setKeyInput('');
    setKeyError(null);
  }

  async function handleSaveKey() {
    if (!keyModalProvider || !keyInput.trim()) return;
    const failed = await saveMyAiKey(keyModalProvider, keyInput.trim());
    if (failed) {
      setKeyError(failed);
      return;
    }
    closeKeyModal();
    setKeySaved(true);
    reconcileActiveProvider();
    setTimeout(() => setKeySaved(false), 2200);
  }

  function reconcileActiveProvider() {
    setSelectedAIProvider(resolveAIProvider());
  }

  async function clearProviderKey(provider: AIProvider) {
    await removeMyAiKey(provider);
    reconcileActiveProvider();
  }

  async function handleClearKey() {
    if (!keyModalProvider) return;
    const failed = await removeMyAiKey(keyModalProvider);
    if (failed) {
      setKeyError(failed);
      return;
    }
    closeKeyModal();
    reconcileActiveProvider();
  }

  function handleSelectAIProvider(id: AIProvider) {
    if (!providerHasApiKey(id) || !setAIProvider(id)) return;
    setSelectedAIProvider(id);
    setAIProviderSaved(true);
    setTimeout(() => setAIProviderSaved(false), 2200);
  }

  useEffect(() => {
    reconcileActiveProvider();
  }, [hasOpenAIKey, hasAnthropicKey]);

  const workspaceId = getCurrentWorkspaceId();

  // Load connections from DB on mount
  const [connections, setConnections] = useState<ConnectorConnection[]>([]);
  const [, setConnectionsLoading] = useState(true);

  // Tracks which connectors the admin has enabled for this workspace (visible in EntitySyncPanel)
  // Key: connector_type; value: enabled (true = is_active row exists)
  const [connectorEnabled, setConnectorEnabled] = useState<Record<string, boolean>>({});
  const [enablingType, setEnablingType] = useState<ConnectorType | null>(null);
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [selectedConnectorType, setSelectedConnectorType] = useState<ConnectorType | null>(null);
  const [isConfigureMode, setIsConfigureMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [engineOnline, setEngineOnline] = useState<boolean | null>(null); // null = checking
  const [slackNotifyChannelId, setSlackNotifyChannelId] = useState('');
  const [slackNotifyChannelName, setSlackNotifyChannelName] = useState<string | null>(null);
  const [slackAvailableChannels, setSlackAvailableChannels] = useState<SlackChannel[]>([]);
  const [slackChannelsLoading, setSlackChannelsLoading] = useState(false);
  
  // Load connections from DB when workspace is available
  useEffect(() => {
    if (!workspaceId) { setConnectionsLoading(false); return; }
    loadConnectionsFromDB(workspaceId).then(conns => {
      setConnections(conns);
      setConnectionsLoading(false);
    });
  }, [workspaceId]);

  // Load workspace-level enabled state from connector_configs
  useEffect(() => {
    if (!workspaceId) return;
    listConnectorConfigs(workspaceId).then(({ data }) => {
      const map: Record<string, boolean> = {};
      for (const row of data) {
        if (row.userId === null) map[row.connectorType] = row.isActive;
      }
      setConnectorEnabled(map);
    });
  }, [workspaceId]);

  // Handle OAuth callbacks (Slack, Google Calendar, Zoom, Google Meet)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const themeParam = urlParams.get('theme');
    if (themeParam === 'light' || themeParam === 'dark') {
      try {
        localStorage.setItem('pp-theme', themeParam);
        document.documentElement.setAttribute('data-theme', themeParam);
        if (themeParam === 'dark') document.documentElement.classList.add('dark');
        else document.documentElement.classList.remove('dark');
      } catch {
        // ignore
      }
    }

    const slackStatus = urlParams.get('slack');
    const googleCalendarStatus = urlParams.get('google_calendar');
    const zoomStatus = urlParams.get('zoom');
    const googleMeetStatus = urlParams.get('google_meet');
    const dropboxStatus = urlParams.get('dropbox');
    const googleDocsStatus = urlParams.get('google_docs');
    const intercomStatus = urlParams.get('intercom');
    const zendeskStatus = urlParams.get('zendesk');
    const hubspotStatus = urlParams.get('hubspot');
    const salesforceStatus = urlParams.get('salesforce');
    const linearStatus = urlParams.get('linear');
    const notionStatus = urlParams.get('notion');
    const oauthError = urlParams.get('error');
    const oauthProvider = urlParams.get('provider');

    // Engine wrote auth_data during OAuth callback — reload from DB, never wipe.
    const reloadAfterOAuth = (type: ConnectorType, label: string) => {
      if (!workspaceId) return;
      void (async () => {
        await new Promise(r => setTimeout(r, 400));
        const conns = await loadConnectionsFromDB(workspaceId);
        setConnections(conns);
        setConnectorEnabled(prev => ({ ...prev, [type]: true }));
        if (!conns.some(c => c.type === type)) {
          setError(`${label} authorized, but credentials are not visible yet. Refresh the page.`);
        }
      })();
      setSuccessMessage(`${label} connected successfully.`);
      setError(null);
      window.history.replaceState({}, '', window.location.pathname);
    };

    const addConnectionIfMissing = (type: ConnectorConnection['type'], name: string) => {
      setConnections(prev => {
        if (prev.find(c => c.type === type)) return prev;
        const next: ConnectorConnection = {
          id: `${type}-${Date.now()}`,
          type,
          name,
          status: 'connected' as const,
          lastSync: new Date().toISOString(),
        };
        // Persist to DB — fire-and-forget (OAuth-originated connections have no auth_data yet)
        if (workspaceId) {
          persistConnection(workspaceId, type, name, {}).then(res => {
            if (res) setConnections(prev2 => prev2.map(c => c.type === type ? { ...c, id: res.id } : c));
          });
        }
        return [...prev, next];
      });
    };

    if (slackStatus === 'connected') {
      // Engine already wrote auth_data via OAuth — reload; never wipe credentials.
      if (workspaceId) {
        void (async () => {
          // Brief delay so we don't race a slow credential write
          await new Promise(r => setTimeout(r, 400));
          const conns = await loadConnectionsFromDB(workspaceId);
          setConnections(conns);
          setConnectorEnabled(prev => ({ ...prev, slack: true }));
          if (!conns.some(c => c.type === 'slack')) {
            setError('Slack authorized, but credentials are not visible yet. Refresh the page.');
          }
        })();
      }
      setSuccessMessage('Slack connected successfully. Admins: set a workspace default under Configure. Everyone: pick a personal channel in My Connectors.');
      setError(null);
      window.history.replaceState({}, '', window.location.pathname);
    } else if (googleCalendarStatus === 'connected') {
      addConnectionIfMissing('google-calendar', 'Google Calendar');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (zoomStatus === 'connected') {
      addConnectionIfMissing('zoom', 'Zoom');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (googleMeetStatus === 'connected') {
      addConnectionIfMissing('google-meet', 'Google Meet');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (dropboxStatus === 'connected' || googleDocsStatus === 'connected') {
      // The engine wrote real credentials (auth_data) during the OAuth callback,
      // so reload from the DB — never overwrite with empty auth_data.
      const type: ConnectorType = dropboxStatus === 'connected' ? 'dropbox' : 'google-docs';
      const label = type === 'dropbox' ? 'Dropbox' : 'Google Docs';
      if (workspaceId) {
        void (async () => {
          await new Promise(r => setTimeout(r, 400));
          const conns = await loadConnectionsFromDB(workspaceId);
          setConnections(conns);
          setConnectorEnabled(prev => ({ ...prev, [type]: true }));
          if (!conns.some(c => c.type === type)) {
            setError(`${label} authorized, but credentials are not visible yet. Refresh the page.`);
          }
        })();
      }
      setSuccessMessage(`${label} connected successfully.`);
      setError(null);
      window.history.replaceState({}, '', window.location.pathname);
    } else if (intercomStatus === 'connected') {
      reloadAfterOAuth('intercom', 'Intercom');
    } else if (zendeskStatus === 'connected') {
      reloadAfterOAuth('zendesk', 'Zendesk');
    } else if (hubspotStatus === 'connected') {
      reloadAfterOAuth('hubspot', 'HubSpot');
    } else if (salesforceStatus === 'connected') {
      reloadAfterOAuth('salesforce', 'Salesforce');
    } else if (linearStatus === 'connected') {
      reloadAfterOAuth('linear', 'Linear');
    } else if (notionStatus === 'connected') {
      reloadAfterOAuth('notion', 'Notion');
    } else if (oauthError) {
      const providerLabel = oauthProvider === 'zoom' ? 'Zoom'
        : oauthProvider === 'google-meet' ? 'Google Meet'
        : oauthProvider === 'dropbox' ? 'Dropbox'
        : oauthProvider === 'google-docs' ? 'Google Docs'
        : oauthProvider === 'intercom' ? 'Intercom'
        : oauthProvider === 'zendesk' ? 'Zendesk'
        : oauthProvider === 'hubspot' ? 'HubSpot'
        : oauthProvider === 'salesforce' ? 'Salesforce'
        : oauthProvider === 'linear' ? 'Linear'
        : oauthProvider === 'notion' ? 'Notion'
        : oauthProvider === 'slack' || oauthError.includes('slack') ? 'Slack'
        : 'Google Calendar';
      const detail =
        oauthError === 'token_exchange_failed'
          ? `${providerLabel} connection failed while saving credentials. Try Connect again.`
          : `${providerLabel} connection failed: ${oauthError}`;
      setError(detail);
      window.history.replaceState({}, '', window.location.pathname);
    }
    // Runs once on mount to handle OAuth return params; workspaceId is captured intentionally.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When Slack is connected, load workspace default notify channel (ambient)
  useEffect(() => {
    if (!workspaceId) return;
    const hasSlack = connections.some(c => c.type === 'slack');
    if (!hasSlack) {
      setSlackNotifyChannelId('');
      setSlackNotifyChannelName(null);
      return;
    }
    const api = getIntegrationsApi(workspaceId);
    api.getSlackStatus()
      .then(status => {
        setSlackNotifyChannelId(status.workspaceNotifyChannelId ?? '');
        setSlackNotifyChannelName(status.workspaceNotifyChannelName ?? null);
      })
      .catch(() => {
        /* non-blocking */
      });
  }, [workspaceId, connections]);

  const loadSlackAvailableChannels = async () => {
    if (!workspaceId) return;
    setSlackChannelsLoading(true);
    try {
      const api = getIntegrationsApi(workspaceId);
      const channels = await api.listSlackChannels();
      setSlackAvailableChannels(channels);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to list Slack channels');
    } finally {
      setSlackChannelsLoading(false);
    }
  };

  // Check whether the integration engine is actually reachable
  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        const res = await fetch(`${INTEGRATIONS_ENGINE_URL}/health`, { signal: AbortSignal.timeout(2000) });
        if (!cancelled) setEngineOnline(res.ok);
      } catch {
        if (!cancelled) setEngineOnline(false);
      }
    };
    check();
    return () => { cancelled = true; };
  }, []);

  // Modal states - Existing integrations
  const [miroToken, setMiroToken] = useState('');
  const [jiraSiteUrl, setJiraSiteUrl] = useState('');
  const [jiraEmail, setJiraEmail] = useState('');
  const [jiraToken, setJiraToken] = useState('');

  // Modal states - Additional integrations
  const [notionToken, setNotionToken] = useState('');
  const [linearApiKey, setLinearApiKey] = useState('');
  // Quantive
  const [quantiveApiKey, setQuantiveApiKey] = useState('');
  const [quantiveAccountId, setQuantiveAccountId] = useState('');
  // Granola
  const [granolaApiKey, setGranolaApiKey] = useState('');
  // Raycast
  const [raycastApiKey, setRaycastApiKey] = useState('');
  // MCP Endpoint
  const [mcpApiKey, setMcpApiKey] = useState('');
  // Figma
  const [figmaAccessToken, setFigmaAccessToken] = useState('');
  const [figmaTeamId, setFigmaTeamId] = useState('');
  // Intercom (support conversations) — OAuth; only the region is entered here
  const [intercomRegion, setIntercomRegion] = useState('us');
  // Zendesk (support tickets) — OAuth; only the subdomain is entered here
  const [zendeskSubdomain, setZendeskSubdomain] = useState('');
  // Salesforce (CRM opportunities) — OAuth; optional login host for sandboxes
  const [salesforceLoginUrl, setSalesforceLoginUrl] = useState('');
  // Datadog
  const [datadogApiKey, setDatadogApiKey] = useState('');
  const [datadogAppKey, setDatadogAppKey] = useState('');
  const [datadogSite, setDatadogSite] = useState('');
  // Snowflake
  const [snowflakeAccount, setSnowflakeAccount] = useState('');
  const [snowflakeUsername, setSnowflakeUsername] = useState('');
  const [snowflakeToken, setSnowflakeToken] = useState('');
  const [snowflakeWarehouse, setSnowflakeWarehouse] = useState('');
  const [snowflakeDatabase, setSnowflakeDatabase] = useState('');
  const [snowflakeRole, setSnowflakeRole] = useState('');

  // Other connectors (currently just CSV which is functional)
  const otherConnectors = CONNECTOR_REGISTRY.filter(c => 
    c.type === 'csv'
  );

  const isConnectorEnabled = (type: ConnectorType) => !!connectorEnabled[type];

  const handleToggleEnabled = async (type: ConnectorType) => {
    if (!workspaceId) return;
    setEnablingType(type);
    const currently = isConnectorEnabled(type);
    // Optimistic update
    setConnectorEnabled(prev => ({ ...prev, [type]: !currently }));
    if (currently) {
      await disableConnector(workspaceId, null, type);
    } else {
      await enableWorkspaceConnector(workspaceId, type);
    }
    setEnablingType(null);
  };

  const handleConnect = async (type: ConnectorType) => {
    resetForm();
    setSelectedConnectorType(type);
    setIsConfigureMode(false);
    setShowConnectModal(true);
    setError(null);
    setSuccessMessage(null);
  };

  const handleConfigure = (type: ConnectorType) => {
    const conn = getConnectionForType(type);
    const cfg = (conn?.config ?? {}) as Record<string, string>;
    // Pre-populate form fields from saved config
    if (type === 'miro')           setMiroToken(cfg.accessToken ?? '');
    else if (type === 'jira')      { setJiraSiteUrl(cfg.siteUrl ?? ''); setJiraEmail(cfg.email ?? ''); setJiraToken(cfg.apiToken ?? ''); }
    else if (type === 'notion')    setNotionToken(cfg.accessToken ?? '');
    else if (type === 'linear')    setLinearApiKey(cfg.apiKey ?? '');
    else if (type === 'quantive')  { setQuantiveApiKey(cfg.apiKey ?? ''); setQuantiveAccountId(cfg.accountId ?? ''); }
    else if (type === 'granola')   setGranolaApiKey(cfg.apiKey ?? '');
    else if (type === 'raycast')   setRaycastApiKey(cfg.apiKey ?? '');
    else if (type === 'mcp')       setMcpApiKey(cfg.apiKey ?? '');
    else if (type === 'figma')     { setFigmaAccessToken(cfg.accessToken ?? ''); setFigmaTeamId(cfg.teamId ?? ''); }
    else if (type === 'slack')     { void loadSlackAvailableChannels(); }
    else if (type === 'intercom')  setIntercomRegion(cfg.region ?? 'us');
    else if (type === 'zendesk')   setZendeskSubdomain(cfg.subdomain ?? '');
    else if (type === 'salesforce') setSalesforceLoginUrl(cfg.loginUrl ?? '');
    else if (type === 'datadog')   { setDatadogApiKey(cfg.apiKey ?? ''); setDatadogAppKey(cfg.appKey ?? ''); setDatadogSite(cfg.site ?? ''); }
    else if (type === 'snowflake') {
      setSnowflakeAccount(cfg.account ?? '');
      setSnowflakeUsername(cfg.username ?? '');
      setSnowflakeToken(cfg.token ?? '');
      setSnowflakeWarehouse(cfg.warehouse ?? '');
      setSnowflakeDatabase(cfg.database ?? '');
      setSnowflakeRole(cfg.role ?? '');
    }
    setSelectedConnectorType(type);
    setIsConfigureMode(true);
    setShowConnectModal(true);
    setError(null);
  };

  const handleDisconnect = (connectionId: string) => {
    const conn = connections.find(c => c.id === connectionId);
    if (conn && workspaceId) {
      removeConnection(workspaceId, conn.type as ConnectorType);
    }
    setConnections(prev => prev.filter(c => c.id !== connectionId));
  };

  const handleSaveConnection = async () => {
    if (!selectedConnectorType) return;
    
    setIsLoading(true);
    setError(null);

    try {
      // In production, this would call the integrations-engine API
      const INTEGRATIONS_API = INTEGRATIONS_ENGINE_URL;
      
      if (selectedConnectorType === 'miro') {
        const response = await fetch(`${INTEGRATIONS_API}/miro/connect`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            workspaceId: workspaceId ?? 'unknown',
            accessToken: miroToken,
          }),
        });
        
        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.error || 'Failed to connect to Miro');
        }
      } else if (selectedConnectorType === 'jira') {
        const response = await fetch(`${INTEGRATIONS_API}/jira/connect`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            workspaceId: workspaceId ?? 'unknown',
            siteUrl: jiraSiteUrl,
            email: jiraEmail,
            apiToken: jiraToken,
          }),
        });
        
        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.error || 'Failed to connect to Jira');
        }
      } else if (selectedConnectorType === 'slack') {
        if (isConfigureMode) {
          if (!slackNotifyChannelId) {
            throw new Error('Choose a workspace default Slack channel for ambient digests');
          }
          const api = getIntegrationsApi(workspaceId ?? 'default');
          const result = await api.setSlackNotifyChannel(slackNotifyChannelId);
          setSlackNotifyChannelName(result.config.channelName);
          setSuccessMessage(`Workspace Slack default set to #${result.config.channelName}`);
          setShowConnectModal(false);
          setIsConfigureMode(false);
          setIsLoading(false);
          return;
        }
        const theme =
          (typeof localStorage !== 'undefined' && localStorage.getItem('pp-theme')) || 'dark';
        const returnUrl = new URL(
          `${window.location.origin}${window.location.pathname}`,
        );
        if (theme === 'light' || theme === 'dark') {
          returnUrl.searchParams.set('theme', theme);
        }
        const response = await fetch(`${INTEGRATIONS_API}/slack/connect`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            workspaceId: workspaceId ?? 'unknown',
            userId: 'current-user',
            redirectUrl: returnUrl.toString(),
          }),
          signal: AbortSignal.timeout(20000),
        });

        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error((data as { error?: string }).error || 'Failed to initiate Slack connection');
        }

        const data = await response.json();
        if (data.authUrl) {
          window.location.href = data.authUrl;
          return;
        }
        throw new Error('Slack connect did not return an authorization URL');
      } else if (selectedConnectorType === 'notion') {
        // OAuth is the primary path: with no token, the engine returns an authUrl
        // to redirect to (Notion public integration). A pasted internal integration
        // token remains the local / single-user fallback and connects directly.
        const theme =
          (typeof localStorage !== 'undefined' && localStorage.getItem('pp-theme')) || 'dark';
        const returnUrl = new URL(`${window.location.origin}${window.location.pathname}`);
        if (theme === 'light' || theme === 'dark') {
          returnUrl.searchParams.set('theme', theme);
        }
        const response = await fetch(`${INTEGRATIONS_API}/notion/connect`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            workspaceId: workspaceId ?? 'unknown',
            redirectUrl: returnUrl.toString(),
            accessToken: notionToken || undefined,
          }),
          signal: AbortSignal.timeout(20000),
        });

        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error((data as { error?: string }).error || 'Failed to connect to Notion');
        }

        const data = await response.json();
        if (data.authUrl) {
          window.location.href = data.authUrl;
          return;
        }
        // No authUrl → internal-token fallback succeeded; fall through to persist path.
      } else if (selectedConnectorType === 'linear') {
        // OAuth is the primary path: with no API key, the engine returns an
        // authUrl to redirect to. A pasted personal API key remains an advanced
        // fallback and connects directly (no redirect).
        const theme =
          (typeof localStorage !== 'undefined' && localStorage.getItem('pp-theme')) || 'dark';
        const returnUrl = new URL(`${window.location.origin}${window.location.pathname}`);
        if (theme === 'light' || theme === 'dark') {
          returnUrl.searchParams.set('theme', theme);
        }
        const response = await fetch(`${INTEGRATIONS_API}/linear/connect`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            workspaceId: workspaceId ?? 'unknown',
            redirectUrl: returnUrl.toString(),
            apiKey: linearApiKey || undefined,
          }),
          signal: AbortSignal.timeout(20000),
        });

        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error((data as { error?: string }).error || 'Failed to connect to Linear');
        }

        const data = await response.json();
        if (data.authUrl) {
          window.location.href = data.authUrl;
          return;
        }
        // No authUrl → API-key fallback succeeded; fall through to persist path.
      } else if (selectedConnectorType === 'quantive') {
        const response = await fetch(`${INTEGRATIONS_API}/quantive/connect`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            workspaceId: workspaceId ?? 'unknown',
            apiKey: quantiveApiKey,
            accountId: quantiveAccountId,
          }),
        });

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.error || 'Failed to connect to Quantive');
        }
      } else if (selectedConnectorType === 'granola') {
        const response = await fetch(`${INTEGRATIONS_API}/granola/connect`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            workspaceId: workspaceId ?? 'unknown',
            apiKey: granolaApiKey,
          }),
        });

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.error || 'Failed to connect to Granola');
        }
      } else if (selectedConnectorType === 'figma') {
        const response = await fetch(`${INTEGRATIONS_API}/figma/connect`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            workspaceId: workspaceId ?? 'unknown',
            accessToken: figmaAccessToken,
            teamId: figmaTeamId || undefined,
          }),
        });

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.error || 'Failed to connect to Figma');
        }
      } else if (selectedConnectorType === 'intercom') {
        // Intercom uses OAuth — kick off the redirect and let the engine
        // callback persist the token.
        const response = await fetch(`${INTEGRATIONS_API}/intercom/connect`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            workspaceId: workspaceId ?? 'unknown',
            region: intercomRegion || 'us',
            redirectUrl: window.location.href,
          }),
          signal: AbortSignal.timeout(20000),
        });

        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error((data as { error?: string }).error || 'Failed to initiate Intercom connection');
        }
        const data = await response.json();
        if (data.authUrl) {
          window.location.href = data.authUrl;
          return;
        }
        throw new Error('Intercom connect did not return an authorization URL');
      } else if (selectedConnectorType === 'zendesk') {
        // Zendesk uses OAuth — kick off the redirect and let the engine
        // callback persist the access + refresh tokens.
        const response = await fetch(`${INTEGRATIONS_API}/zendesk/connect`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            workspaceId: workspaceId ?? 'unknown',
            subdomain: zendeskSubdomain,
            redirectUrl: window.location.href,
          }),
          signal: AbortSignal.timeout(20000),
        });

        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error((data as { error?: string }).error || 'Failed to initiate Zendesk connection');
        }
        const data = await response.json();
        if (data.authUrl) {
          window.location.href = data.authUrl;
          return;
        }
        throw new Error('Zendesk connect did not return an authorization URL');
      } else if (selectedConnectorType === 'hubspot') {
        // HubSpot uses OAuth (public app) — kick off the redirect and let the
        // engine callback persist the access + refresh tokens and portal ID.
        const response = await fetch(`${INTEGRATIONS_API}/hubspot/connect`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            workspaceId: workspaceId ?? 'unknown',
            redirectUrl: window.location.href,
          }),
          signal: AbortSignal.timeout(20000),
        });

        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error((data as { error?: string }).error || 'Failed to initiate HubSpot connection');
        }
        const data = await response.json();
        if (data.authUrl) {
          window.location.href = data.authUrl;
          return;
        }
        throw new Error('HubSpot connect did not return an authorization URL');
      } else if (selectedConnectorType === 'salesforce') {
        // Salesforce uses the OAuth Web Server flow — kick off the redirect and
        // let the engine callback persist the access + refresh tokens and the
        // per-org instance URL. loginUrl is optional (sandbox override).
        const response = await fetch(`${INTEGRATIONS_API}/salesforce/connect`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            workspaceId: workspaceId ?? 'unknown',
            loginUrl: salesforceLoginUrl || undefined,
            redirectUrl: window.location.href,
          }),
          signal: AbortSignal.timeout(20000),
        });

        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error((data as { error?: string }).error || 'Failed to initiate Salesforce connection');
        }
        const data = await response.json();
        if (data.authUrl) {
          window.location.href = data.authUrl;
          return;
        }
        throw new Error('Salesforce connect did not return an authorization URL');
      } else if (selectedConnectorType === 'datadog') {
        const response = await fetch(`${INTEGRATIONS_API}/datadog/connect`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            workspaceId: workspaceId ?? 'unknown',
            apiKey: datadogApiKey,
            appKey: datadogAppKey,
            site: datadogSite || undefined,
          }),
        });

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.error || 'Failed to connect to Datadog');
        }
      } else if (selectedConnectorType === 'snowflake') {
        const response = await fetch(`${INTEGRATIONS_API}/snowflake/connect`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            workspaceId: workspaceId ?? 'unknown',
            account: snowflakeAccount,
            username: snowflakeUsername,
            token: snowflakeToken,
            warehouse: snowflakeWarehouse || undefined,
            database: snowflakeDatabase || undefined,
            role: snowflakeRole || undefined,
          }),
        });

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.error || 'Failed to connect to Snowflake');
        }
      } else if (selectedConnectorType === 'google-calendar') {
        // Google Calendar uses OAuth flow - redirect to Google
        const response = await fetch(`${INTEGRATIONS_API}/google-calendar/connect`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            workspaceId: workspaceId ?? 'unknown',
            userId: 'current-user',
            redirectUrl: window.location.href,
          }),
        });
        
        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.error || 'Failed to initiate Google Calendar connection');
        }
        
        const data = await response.json();
        if (data.authUrl) {
          window.location.href = data.authUrl;
          return;
        }
      } else if (selectedConnectorType === 'zoom') {
        // Zoom uses OAuth flow
        const response = await fetch(`${INTEGRATIONS_API}/zoom/connect`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            workspaceId: workspaceId ?? 'unknown',
            userId: 'current-user',
            redirectUrl: `${window.location.href}${window.location.search ? '&' : '?'}zoom=pending`,
          }),
        });
        
        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.error || 'Failed to initiate Zoom connection');
        }
        
        const data = await response.json();
        if (data.authUrl) {
          window.location.href = data.authUrl;
          return;
        }
      } else if (selectedConnectorType === 'google-meet') {
        // Google Meet uses OAuth flow via Google
        const response = await fetch(`${INTEGRATIONS_API}/google-meet/connect`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            workspaceId: workspaceId ?? 'unknown',
            userId: 'current-user',
            redirectUrl: window.location.href,
          }),
        });
        
        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.error || 'Failed to initiate Google Meet connection');
        }
        
        const data = await response.json();
        if (data.authUrl) {
          window.location.href = data.authUrl;
          return;
        }
      } else if (selectedConnectorType === 'dropbox' || selectedConnectorType === 'google-docs') {
        // Content data sources use the OAuth authorization-code flow.
        const response = await fetch(`${INTEGRATIONS_API}/${selectedConnectorType}/connect`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            workspaceId: workspaceId ?? 'unknown',
            userId: 'current-user',
            redirectUrl: window.location.href,
          }),
        });

        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error((data as { error?: string }).error || `Failed to initiate ${selectedConnectorType} connection`);
        }

        const data = await response.json();
        if (data.authUrl) {
          window.location.href = data.authUrl;
          return;
        }
        throw new Error(`${selectedConnectorType} connect did not return an authorization URL`);
      }

      // Add to connections
      const connectorNames: Record<string, string> = {
        miro: 'Miro',
        jira: 'Jira',
        slack: 'Slack',
        notion: 'Notion',
        linear: 'Linear',
        'google-calendar': 'Google Calendar',
        zoom: 'Zoom',
        'google-meet': 'Google Meet',
        quantive: 'Quantive',
        granola: 'Granola',
        raycast: 'Raycast',
        mcp: 'MCP Endpoint',
        figma: 'Figma',
        dropbox: 'Dropbox',
        'google-docs': 'Google Docs',
        datadog: 'Datadog',
        snowflake: 'Snowflake',
      };

      // Build the credential config to persist alongside the connection so
      // "Configure" can pre-populate the fields next time.
      // In configure mode, keep the existing value for any field left blank.
      const existingConfig = (getConnectionForType(selectedConnectorType)?.config ?? {}) as Record<string, string>;
      const credentialConfig: Record<string, string> = ((): Record<string, string> => {
        switch (selectedConnectorType) {
          case 'miro':    return { accessToken: miroToken || existingConfig.accessToken || '' };
          case 'jira':    return {
            siteUrl: jiraSiteUrl || existingConfig.siteUrl || '',
            email: jiraEmail || existingConfig.email || '',
            apiToken: jiraToken || existingConfig.apiToken || '',
          };
          // slack always returns or throws above (OAuth redirect, or the
          // configure-mode notify-channel branch) and never reaches this
          // persist path — kept out of the switch so TS can prove exhaustiveness.
          case 'notion':  return { accessToken: notionToken || existingConfig.accessToken || '' };
          // The engine saved this key as `accessToken` when it validated it;
          // this persist replaces auth_data, so it carries the engine's shape
          // too — otherwise the engine finds no token and reports "not
          // connected" (the 2026-10-15 defect).
          case 'linear': {
            const linearKey = linearApiKey || existingConfig.apiKey || existingConfig.accessToken || '';
            return linearKey ? { apiKey: linearKey, accessToken: linearKey, authMethod: 'api_key' } : {};
          }
          case 'quantive':  return {
            apiKey: quantiveApiKey || existingConfig.apiKey || '',
            accountId: quantiveAccountId || existingConfig.accountId || '',
          };
          case 'granola':   return { apiKey: granolaApiKey || existingConfig.apiKey || '' };
          case 'raycast':   return { apiKey: raycastApiKey || existingConfig.apiKey || '' };
          case 'mcp':       return { apiKey: mcpApiKey || existingConfig.apiKey || '' };
          // dropbox and google-docs always return or throw in the OAuth
          // branch above and never reach this persist path.
          case 'figma':     return {
            accessToken: figmaAccessToken || existingConfig.accessToken || '',
            teamId: figmaTeamId || existingConfig.teamId || '',
          };
          // intercom & zendesk connect via OAuth redirect and never reach this
          // persist path; the engine callback writes their auth_data.
          case 'datadog':   return {
            apiKey: datadogApiKey || existingConfig.apiKey || '',
            appKey: datadogAppKey || existingConfig.appKey || '',
            site: datadogSite || existingConfig.site || '',
          };
          case 'snowflake': return {
            account: snowflakeAccount || existingConfig.account || '',
            username: snowflakeUsername || existingConfig.username || '',
            token: snowflakeToken || existingConfig.token || '',
            warehouse: snowflakeWarehouse || existingConfig.warehouse || '',
            database: snowflakeDatabase || existingConfig.database || '',
            role: snowflakeRole || existingConfig.role || '',
          };
          default:        return {};
        }
      })();

      const existingConn = getConnectionForType(selectedConnectorType);
      const connDisplayName = connectorNames[selectedConnectorType] || selectedConnectorType;

      // Persist to DB — §2.1 compliance. auth_data holds credential refs;
      // raw tokens stored here until a secrets vault is wired in Phase 2.
      let persistedId = existingConn?.id ?? `${selectedConnectorType}-${Date.now()}`;
      if (workspaceId) {
        const saved = await persistConnection(workspaceId, selectedConnectorType, connDisplayName, credentialConfig);
        if (saved) persistedId = saved.id;
        // Ensure the connector is marked enabled at workspace level
        await enableWorkspaceConnector(workspaceId, selectedConnectorType, connDisplayName);
        setConnectorEnabled(prev => ({ ...prev, [selectedConnectorType]: true }));
      }

      const newConnection: ConnectorConnection = {
        id: persistedId,
        type: selectedConnectorType,
        name: connDisplayName,
        status: 'connected',
        lastSync: new Date().toISOString(),
        config: credentialConfig,
      };
      
      setConnections(prev => [...prev.filter(c => c.type !== selectedConnectorType), newConnection]);
      setShowConnectModal(false);
      resetForm();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Connection failed');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setMiroToken('');
    setJiraSiteUrl('');
    setJiraEmail('');
    setJiraToken('');
    setNotionToken('');
    setLinearApiKey('');
    setQuantiveApiKey('');
    setQuantiveAccountId('');
    setGranolaApiKey('');
    setRaycastApiKey('');
    setMcpApiKey('');
    setFigmaAccessToken('');
    setFigmaTeamId('');
    setIntercomRegion('us');
    setZendeskSubdomain('');
    setSalesforceLoginUrl('');
    setDatadogApiKey('');
    setDatadogAppKey('');
    setDatadogSite('');
    setSnowflakeAccount('');
    setSnowflakeUsername('');
    setSnowflakeToken('');
    setSnowflakeWarehouse('');
    setSnowflakeDatabase('');
    setSnowflakeRole('');
    setSelectedConnectorType(null);
    setIsConfigureMode(false);
  };

  const getConnectionForType = (type: ConnectorType) => {
    return connections.find(c => c.type === type);
  };

  /**
   * Three-state status badge:
   *   Connected  — active row with credentials
   *   Available  — workspace-enabled (toggle on) but no credentials yet
   *   (nothing)  — connector is off
   */
  const getConnectorBadge = (type: ConnectorType) => {
    if (getConnectionForType(type)) {
      return (
        <span className="flex items-center gap-1 text-sm text-[var(--green)] dark:text-[var(--text-ter)] bg-[var(--green-dim)] px-2 py-1 rounded-full">
          <Check className="h-3.5 w-3.5" />
          Connected
        </span>
      );
    }
    if (isConnectorEnabled(type)) {
      return (
        <span className="flex items-center gap-1 text-sm text-[var(--text-sec)] bg-[var(--slate-dim)] px-2 py-1 rounded-full">
          <Link2 className="h-3.5 w-3.5" />
          Available
        </span>
      );
    }
    return null;
  };

  const AIConnectionBadge = ({ connected }: { connected: boolean }) => (
    connected ? (
      <span className="flex items-center gap-1 text-sm text-[var(--green)] dark:text-[var(--text-ter)] bg-[var(--green-dim)] px-2 py-1 rounded-full">
        <Check className="h-3.5 w-3.5" />
        Connected
      </span>
    ) : (
      <span className="flex items-center gap-1 text-sm text-[var(--text-ter)] bg-[var(--hover-tint)] px-2 py-1 rounded-full">
        Not connected
      </span>
    )
  );

  const AIActiveBadge = ({ accent }: { accent: 'green' | 'amber' }) => {
    const cls = accent === 'green'
      ? 'text-[var(--green)] dark:text-[var(--text-ter)] bg-[var(--green-dim)]'
      : 'text-[var(--mustard)] bg-[var(--mustard-dim)]';
    return (
      <span className={`flex items-center gap-1 text-sm px-2 py-1 rounded-full ${cls}`}>
        <Check className="h-3.5 w-3.5" />
        Active provider
      </span>
    );
  };

  const scrollToConnector = (id: string) => {
    const el = document.getElementById(`connector-${id}`);
    if (!el) return;
    // `block: 'start'` so nearby cards (ChatGPT / Claude) still move the
    // viewport; `center` often no-ops when the target is already on screen.
    el.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
  };

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<Link2Icon className="h-4 w-4 text-primary-600" />}
        iconBg="bg-primary-50"
        title="Connectors"
        subtitle="Connect external tools to import ideas and export priorities."
      />

      {/* Featured Integrations — compact jump strip to live cards below */}
      <div className="mb-2">
        <h2 className="text-lg font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)] mb-1">Featured Integrations</h2>
        <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-ter)] mb-4">
          Jump to the connectors teams use most often.
        </p>
        <div className="space-y-2">
          {(
            [
              [
                { id: 'chatgpt', label: 'ChatGPT', icon: <ChatGPTLogo /> },
                { id: 'claude', label: 'Claude', icon: <ClaudeLogo /> },
                { id: 'jira', label: 'Jira', icon: ConnectorIcons.jira },
                { id: 'miro', label: 'Miro', icon: ConnectorIcons.miro },
                { id: 'slack', label: 'Slack', icon: ConnectorIcons.slack },
              ],
              [
                { id: 'salesforce', label: 'Salesforce', icon: ConnectorIcons.salesforce },
                { id: 'datadog', label: 'Datadog', icon: ConnectorIcons.datadog },
                { id: 'snowflake', label: 'Snowflake', icon: ConnectorIcons.snowflake },
                { id: 'intercom', label: 'Intercom', icon: ConnectorIcons.intercom },
                { id: 'zendesk', label: 'Zendesk', icon: ConnectorIcons.zendesk },
              ],
            ] as Array<Array<{ id: string; label: string; icon: ReactNode }>>
          ).map((row, rowIdx) => (
            <div key={rowIdx} className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
              {row.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => scrollToConnector(item.id)}
                  className="flex items-center gap-2.5 px-3 py-2.5 border text-left transition-colors bg-[var(--bg-raised)] border-[var(--rule)] text-[var(--text-pri)] hover:bg-[var(--active-tint)] hover:border-[var(--rule-bright)]"
                >
                  <span className="flex-shrink-0 scale-75 origin-left -mr-1">{item.icon}</span>
                  <span className="text-sm font-medium truncate">{item.label}</span>
                </button>
              ))}
            </div>
          ))}
        </div>
      </div>

      <div
        role="separator"
        aria-hidden="true"
        className="mt-10 mb-10 border-t border-[var(--rule)]"
      />

      {/* Live Integrations — categorized */}
      <div className="mb-10 space-y-10">
        <div>
          <h2 className="text-lg font-semibold text-[var(--text-pri)] mb-1">AI & Automation</h2>
          <p className="text-sm text-[var(--text-ter)] mb-4">Configure workspace LLMs and connect tools that capture meeting context or let you act on priorities without leaving your flow.</p>
        <p className="text-sm text-[var(--text-ter)] mb-4">
          Workspace-level AI defaults (admin). Individual users can set personal keys under{' '}
          <Link to="/prioritization/settings/my-connectors" className="text-primary-600 hover:underline">
            My Account → My Connectors
          </Link>
          .
        </p>

        {(aiProviderSaved || keySaved) && (
          <div className="mb-3 flex items-center gap-2 text-sm text-[var(--green)] dark:text-[var(--text-ter)]">
            <Check className="h-4 w-4 text-[var(--green)]" />
            <span className="font-medium">
              {keySaved ? 'API key saved. AI features will use this key.' : 'AI provider updated. New requests will use this provider.'}
            </span>
          </div>
        )}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">

          {/* ChatGPT Card */}
          {(() => {
            const isActive = selectedAIProvider === 'openai';
            return (
              <div id="connector-chatgpt" className={`scroll-mt-6 bg-[var(--bg-raised)]  border-2 p-6 transition-all ${isActive ? 'border-[var(--green)]' : 'border-[var(--rule)] hover:border-primary-300 hover:shadow-md'}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-4">
                    <div className={`flex h-12 w-12 flex-shrink-0 items-center justify-center  ${isActive ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]' : 'bg-[var(--hover-tint)] text-[var(--text-sec)]'}`}>
                      <ChatGPTLogo />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[var(--text-pri)]">ChatGPT</h3>
                      <p className="text-sm text-[var(--text-ter)]">OpenAI GPT models</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center justify-end gap-2">
                    <AIConnectionBadge connected={hasOpenAIKey} />
                    {isActive && <AIActiveBadge accent="green" />}
                  </div>
                </div>
                <p className="mt-3 text-sm text-[var(--text-sec)]">
                  Powers clustering, item work, idea crafting, and LLM-assisted features across the app using OpenAI's GPT models.
                </p>

                {hasOpenAIKey && openaiKeyPreview ? (
                  <div className="mt-3 flex items-center gap-2 text-xs px-3 py-2 bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]">
                    <Check className="h-3.5 w-3.5 flex-shrink-0" />
                    <span className="font-mono">{openaiKeyPreview}</span>
                  </div>
                ) : (
                  <div className="mt-3 flex items-center gap-2 text-xs px-3 py-2 bg-[var(--mustard-dim)] text-[var(--mustard)] border border-[var(--mustard)]">
                    <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
                    <span>Add an API key to connect. AI features need a connected provider.</span>
                  </div>
                )}

                <div className="mt-4 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => openKeyModal('openai')}
                    className="flex items-center gap-2 px-3 py-2 text-sm bg-[var(--hover-tint)] hover:bg-[var(--hover-tint)] transition-colors"
                  >
                    <Settings className="h-4 w-4" />
                    {hasOpenAIKey ? 'Update key' : 'Add API key'}
                  </button>
                  {hasSavedAiKey('openai') && (
                    <button
                      type="button"
                      onClick={() => void clearProviderKey('openai')}
                      className="flex items-center gap-2 px-3 py-2 text-sm text-[var(--coral)] hover:bg-[var(--coral-dim)] transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                      Remove key
                    </button>
                  )}
                  {!isActive && hasOpenAIKey && (
                    <button
                      type="button"
                      onClick={() => handleSelectAIProvider('openai')}
                      className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white hover:bg-primary-700 transition-colors"
                    >
                      Set as active provider
                    </button>
                  )}
                </div>
              </div>
            );
          })()}


          {/* Claude Card */}
          {(() => {
            const isActive = selectedAIProvider === 'anthropic';
            return (
              <div id="connector-claude" className={`scroll-mt-6 bg-[var(--bg-raised)]  border-2 p-6 transition-all ${isActive ? 'border-[var(--mustard)]' : 'border-[var(--rule)] hover:border-primary-300 hover:shadow-md'}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-4">
                    <div className={`flex h-12 w-12 flex-shrink-0 items-center justify-center  ${isActive ? 'bg-[var(--mustard-dim)] text-[var(--mustard)]' : 'bg-[var(--hover-tint)] text-[var(--text-sec)]'}`}>
                      <ClaudeLogo />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[var(--text-pri)]">Claude</h3>
                      <p className="text-sm text-[var(--text-ter)]">Anthropic Claude models</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center justify-end gap-2">
                    <AIConnectionBadge connected={hasAnthropicKey} />
                    {isActive && <AIActiveBadge accent="amber" />}
                  </div>
                </div>
                <p className="mt-3 text-sm text-[var(--text-sec)]">
                  Anthropic's Claude models — known for nuanced reasoning and long-context tasks. A strong alternative for analysis-heavy workflows.
                </p>

                {hasAnthropicKey && anthropicKeyPreview ? (
                  <div className="mt-3 flex items-center gap-2 text-xs px-3 py-2 bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]">
                    <Check className="h-3.5 w-3.5 flex-shrink-0" />
                    <span className="font-mono">{anthropicKeyPreview}</span>
                  </div>
                ) : (
                  <div className="mt-3 flex items-center gap-2 text-xs px-3 py-2 bg-[var(--mustard-dim)] text-[var(--mustard)] border border-[var(--mustard)]">
                    <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
                    <span>Add an API key to connect. Set as active provider when ready.</span>
                  </div>
                )}

                <div className="mt-4 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => openKeyModal('anthropic')}
                    className="flex items-center gap-2 px-3 py-2 text-sm bg-[var(--hover-tint)] hover:bg-[var(--hover-tint)] transition-colors"
                  >
                    <Settings className="h-4 w-4" />
                    {hasAnthropicKey ? 'Update key' : 'Add API key'}
                  </button>
                  {hasSavedAiKey('anthropic') && (
                    <button
                      type="button"
                      onClick={() => void clearProviderKey('anthropic')}
                      className="flex items-center gap-2 px-3 py-2 text-sm text-[var(--coral)] hover:bg-[var(--coral-dim)] transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                      Remove key
                    </button>
                  )}
                  {!isActive && hasAnthropicKey && (
                    <button
                      type="button"
                      onClick={() => handleSelectAIProvider('anthropic')}
                      className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white hover:bg-primary-700 transition-colors"
                    >
                      Set as active provider
                    </button>
                  )}
                </div>
              </div>
            );
          })()}


          {/* Granola Card */}
          <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6 hover:border-primary-300 hover:shadow-md transition-all">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                {ConnectorIcons.granola}
                <div>
                  <h3 className="font-semibold text-[var(--text-pri)]">Granola</h3>
                  <p className="text-sm text-[var(--text-ter)]">Trace items to meetings</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {getConnectorBadge('granola')}
                <button
                  onClick={() => handleToggleEnabled('granola')}
                  disabled={enablingType === 'granola'}
                  title={isConnectorEnabled('granola') ? 'Disable for workspace' : 'Enable for workspace'}
                  className={`relative w-9 h-5 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 disabled:opacity-50 ${isConnectorEnabled('granola') ? 'bg-primary-600' : 'bg-[var(--rule-bright)]'}`}
                  role="switch"
                  aria-checked={isConnectorEnabled('granola')}
                >
                  <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-[var(--bg-raised)] rounded-full shadow transition-transform ${isConnectorEnabled('granola') ? 'translate-x-4' : 'translate-x-0'}`} />
                </button>
              </div>
            </div>

            <div className="mt-4 flex items-center gap-2 text-sm text-[var(--text-ter)]">
              <ArrowDownToLine className="h-4 w-4" />
              <span>Import</span>
            </div>

            <p className="mt-3 text-sm text-[var(--text-sec)]">
              Link priorities.ai items to the Granola meeting notes where decisions were made.
              Trace every initiative back to the conversation that originated it.
            </p>

            <div className="mt-4 flex gap-2">
              {getConnectionForType('granola') ? (
                <>
                  <button
                    onClick={() => handleConfigure('granola')}
                    className="flex items-center gap-2 px-3 py-2 text-sm bg-[var(--hover-tint)] hover:bg-[var(--hover-tint)] transition-colors"
                  >
                    <Settings className="h-4 w-4" />
                    Configure
                  </button>
                  <button
                    onClick={() => {
                      const conn = getConnectionForType('granola');
                      if (conn) handleDisconnect(conn.id);
                    }}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-[var(--coral)] hover:bg-[var(--coral-dim)] transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                    Disconnect
                  </button>
                </>
              ) : (
                <button
                  onClick={() => handleConnect('granola')}
                  className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white hover:bg-primary-700 transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  Connect Granola
                </button>
              )}
            </div>
          </div>


          {/* Raycast Card */}
          <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6 hover:border-primary-300 hover:shadow-md transition-all">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                {ConnectorIcons.raycast}
                <div>
                  <h3 className="font-semibold text-[var(--text-pri)]">Raycast</h3>
                  <p className="text-sm text-[var(--text-ter)]">Keyboard-first backlog actions</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {getConnectorBadge('raycast')}
                <button
                  onClick={() => handleToggleEnabled('raycast')}
                  disabled={enablingType === 'raycast'}
                  title={isConnectorEnabled('raycast') ? 'Disable for workspace' : 'Enable for workspace'}
                  className={`relative w-9 h-5 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 disabled:opacity-50 ${isConnectorEnabled('raycast') ? 'bg-primary-600' : 'bg-[var(--rule-bright)]'}`}
                  role="switch"
                  aria-checked={isConnectorEnabled('raycast')}
                >
                  <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-[var(--bg-raised)] rounded-full shadow transition-transform ${isConnectorEnabled('raycast') ? 'translate-x-4' : 'translate-x-0'}`} />
                </button>
              </div>
            </div>

            <div className="mt-4 flex items-center gap-2 text-sm text-[var(--text-ter)]">
              <RefreshCcw className="h-4 w-4" />
              <span>Import + Export</span>
            </div>

            <p className="mt-3 text-sm text-[var(--text-sec)]">
              Add items, search priorities, and act on your backlog from the Raycast launcher —
              keyboard-first, with zero context-switching.
            </p>

            <div className="mt-4 flex gap-2">
              {getConnectionForType('raycast') ? (
                <>
                  <button
                    onClick={() => handleConfigure('raycast')}
                    className="flex items-center gap-2 px-3 py-2 text-sm bg-[var(--hover-tint)] hover:bg-[var(--hover-tint)] transition-colors"
                  >
                    <Settings className="h-4 w-4" />
                    Configure
                  </button>
                  <button
                    onClick={() => {
                      const conn = getConnectionForType('raycast');
                      if (conn) handleDisconnect(conn.id);
                    }}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-[var(--coral)] hover:bg-[var(--coral-dim)] transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                    Disconnect
                  </button>
                </>
              ) : (
                <button
                  onClick={() => handleConnect('raycast')}
                  className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white hover:bg-primary-700 transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  Connect Raycast
                </button>
              )}
            </div>
          </div>

          {/* MCP Endpoint Card */}
          <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6 hover:border-primary-300 hover:shadow-md transition-all">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                {ConnectorIcons.mcp}
                <div>
                  <h3 className="font-semibold text-[var(--text-pri)]">MCP Endpoint</h3>
                  <p className="text-sm text-[var(--text-ter)]">Agent and IDE backlog access</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {getConnectorBadge('mcp')}
                <button
                  onClick={() => handleToggleEnabled('mcp')}
                  disabled={enablingType === 'mcp'}
                  title={isConnectorEnabled('mcp') ? 'Disable for workspace' : 'Enable for workspace'}
                  className={`relative w-9 h-5 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 disabled:opacity-50 ${isConnectorEnabled('mcp') ? 'bg-primary-600' : 'bg-[var(--rule-bright)]'}`}
                  role="switch"
                  aria-checked={isConnectorEnabled('mcp')}
                >
                  <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-[var(--bg-raised)] rounded-full shadow transition-transform ${isConnectorEnabled('mcp') ? 'translate-x-4' : 'translate-x-0'}`} />
                </button>
              </div>
            </div>

            <div className="mt-4 flex items-center gap-2 text-sm text-[var(--text-ter)]">
              <RefreshCcw className="h-4 w-4" />
              <span>Import + Export</span>
            </div>

            <p className="mt-3 text-sm text-[var(--text-sec)]">
              Expose priorities as a Model Context Protocol resource — any AI agent or IDE (Cursor, Claude, and others) can query and update your backlog natively.
            </p>

            <div className="mt-4 flex gap-2">
              {getConnectionForType('mcp') ? (
                <>
                  <button
                    onClick={() => handleConfigure('mcp')}
                    className="flex items-center gap-2 px-3 py-2 text-sm bg-[var(--hover-tint)] hover:bg-[var(--hover-tint)] transition-colors"
                  >
                    <Settings className="h-4 w-4" />
                    Configure
                  </button>
                  <button
                    onClick={() => {
                      const conn = getConnectionForType('mcp');
                      if (conn) handleDisconnect(conn.id);
                    }}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-[var(--coral)] hover:bg-[var(--coral-dim)] transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                    Disconnect
                  </button>
                </>
              ) : (
                <button
                  onClick={() => handleConnect('mcp')}
                  className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white hover:bg-primary-700 transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  Connect MCP Endpoint
                </button>
              )}
            </div>
          </div>

          </div>
        </div>
        <div>
          <h2 className="text-lg font-semibold text-[var(--text-pri)] mb-1">Design & Visual Planning</h2>
          <p className="text-sm text-[var(--text-ter)] mb-4">Import workshop output and design artifacts as items — keep visual context attached to what gets prioritized.</p>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">

          {/* Miro Card */}
          <div id="connector-miro" className="scroll-mt-6 bg-[var(--bg-raised)] border border-[var(--rule)] p-6 hover:border-primary-300 hover:shadow-md transition-all">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                {ConnectorIcons.miro}
                <div>
                  <h3 className="font-semibold text-[var(--text-pri)]">Miro</h3>
                  <p className="text-sm text-[var(--text-ter)]">Import sticky notes as ideas</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {getConnectorBadge('miro')}
                {/* Workspace enable/disable toggle */}
                <button
                  onClick={() => handleToggleEnabled('miro')}
                  disabled={enablingType === 'miro'}
                  title={isConnectorEnabled('miro') ? 'Disable for workspace' : 'Enable for workspace'}
                  className={`relative w-9 h-5 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 disabled:opacity-50 ${isConnectorEnabled('miro') ? 'bg-primary-600' : 'bg-[var(--rule-bright)]'}`}
                  role="switch"
                  aria-checked={isConnectorEnabled('miro')}
                >
                  <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-[var(--bg-raised)] rounded-full shadow transition-transform ${isConnectorEnabled('miro') ? 'translate-x-4' : 'translate-x-0'}`} />
                </button>
              </div>
            </div>
            
            <div className="mt-4 flex items-center gap-2 text-sm text-[var(--text-ter)]">
              <ArrowDownToLine className="h-4 w-4" />
              <span>Import only</span>
            </div>
            
            <p className="mt-3 text-sm text-[var(--text-sec)]">
              Import sticky notes from Miro brainstorming boards directly into your prioritization workflow. 
              Colors map to categories automatically.
            </p>
            
            <div className="mt-4 flex gap-2">
              {getConnectionForType('miro') ? (
                <>
                  <button
                    onClick={() => handleConfigure('miro')}
                    className="flex items-center gap-2 px-3 py-2 text-sm bg-[var(--hover-tint)] hover:bg-[var(--hover-tint)] transition-colors"
                  >
                    <Settings className="h-4 w-4" />
                    Configure
                  </button>
                  <button
                    onClick={() => {
                      const conn = getConnectionForType('miro');
                      if (conn) handleDisconnect(conn.id);
                    }}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-[var(--coral)] hover:bg-[var(--coral-dim)] transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                    Disconnect
                  </button>
                </>
              ) : (
                <button
                  onClick={() => handleConnect('miro')}
                  className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white hover:bg-primary-700 transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  Connect Miro
                </button>
              )}
            </div>

          </div>


          {/* Figma Card */}
          <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6 hover:border-primary-300 hover:shadow-md transition-all">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                {ConnectorIcons.figma}
                <div>
                  <h3 className="font-semibold text-[var(--text-pri)]">Figma</h3>
                  <p className="text-sm text-[var(--text-ter)]">Import frames, link designs</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {getConnectorBadge('figma')}
                <button
                  onClick={() => handleToggleEnabled('figma')}
                  disabled={enablingType === 'figma'}
                  title={isConnectorEnabled('figma') ? 'Disable for workspace' : 'Enable for workspace'}
                  className={`relative w-9 h-5 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 disabled:opacity-50 ${isConnectorEnabled('figma') ? 'bg-primary-600' : 'bg-[var(--rule-bright)]'}`}
                  role="switch"
                  aria-checked={isConnectorEnabled('figma')}
                >
                  <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-[var(--bg-raised)] rounded-full shadow transition-transform ${isConnectorEnabled('figma') ? 'translate-x-4' : 'translate-x-0'}`} />
                </button>
              </div>
            </div>

            <div className="mt-4 flex items-center gap-2 text-sm text-[var(--text-ter)]">
              <RefreshCcw className="h-4 w-4" />
              <span>Import + Export</span>
            </div>

            <p className="mt-3 text-sm text-[var(--text-sec)]">
              Import Figma frames as prioritization items, export priorities back as comments,
              and link individual items to specific Figma frames for design traceability.
            </p>

            <div className="mt-4 flex gap-2">
              {getConnectionForType('figma') ? (
                <>
                  <button
                    onClick={() => handleConfigure('figma')}
                    className="flex items-center gap-2 px-3 py-2 text-sm bg-[var(--hover-tint)] hover:bg-[var(--hover-tint)] transition-colors"
                  >
                    <Settings className="h-4 w-4" />
                    Configure
                  </button>
                  <button
                    onClick={() => {
                      const conn = getConnectionForType('figma');
                      if (conn) handleDisconnect(conn.id);
                    }}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-[var(--coral)] hover:bg-[var(--coral-dim)] transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                    Disconnect
                  </button>
                </>
              ) : (
                <button
                  onClick={() => handleConnect('figma')}
                  className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white hover:bg-primary-700 transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  Connect Figma
                </button>
              )}
            </div>
          </div>

          </div>
        </div>
        <div>
          <h2 className="text-lg font-semibold text-[var(--text-pri)] mb-1">Work Management</h2>
          <p className="text-sm text-[var(--text-ter)] mb-4">Import structured work and push prioritized outcomes to where engineering and product delivery gets tracked.</p>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">

          {/* Jira Card */}
          <div id="connector-jira" className="scroll-mt-6 bg-[var(--bg-raised)] border border-[var(--rule)] p-6 hover:border-primary-300 hover:shadow-md transition-all">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                {ConnectorIcons.jira}
                <div>
                  <h3 className="font-semibold text-[var(--text-pri)]">Jira</h3>
                  <p className="text-sm text-[var(--text-ter)]">Export priorities as epics</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {getConnectorBadge('jira')}
                <button
                  onClick={() => handleToggleEnabled('jira')}
                  disabled={enablingType === 'jira'}
                  title={isConnectorEnabled('jira') ? 'Disable for workspace' : 'Enable for workspace'}
                  className={`relative w-9 h-5 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 disabled:opacity-50 ${isConnectorEnabled('jira') ? 'bg-primary-600' : 'bg-[var(--rule-bright)]'}`}
                  role="switch"
                  aria-checked={isConnectorEnabled('jira')}
                >
                  <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-[var(--bg-raised)] rounded-full shadow transition-transform ${isConnectorEnabled('jira') ? 'translate-x-4' : 'translate-x-0'}`} />
                </button>
              </div>
            </div>
            
            <div className="mt-4 flex items-center gap-2 text-sm text-[var(--text-ter)]">
              <ArrowUpFromLine className="h-4 w-4" />
              <span>Export (+ Import)</span>
            </div>
            
            <p className="mt-3 text-sm text-[var(--text-sec)]">
              Export your prioritized items directly to Jira as epics. Includes rank, score, and 
              category metadata as labels.
            </p>
            
            <div className="mt-4 flex gap-2">
              {getConnectionForType('jira') ? (
                <>
                  <button
                    onClick={() => handleConfigure('jira')}
                    className="flex items-center gap-2 px-3 py-2 text-sm bg-[var(--hover-tint)] hover:bg-[var(--hover-tint)] transition-colors"
                  >
                    <Settings className="h-4 w-4" />
                    Configure
                  </button>
                  <button
                    onClick={() => {
                      const conn = getConnectionForType('jira');
                      if (conn) handleDisconnect(conn.id);
                    }}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-[var(--coral)] hover:bg-[var(--coral-dim)] transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                    Disconnect
                  </button>
                </>
              ) : (
                <button
                  onClick={() => handleConnect('jira')}
                  className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white hover:bg-primary-700 transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  Connect Jira
                </button>
              )}
            </div>
          </div>


          {/* Quantive Card */}
          <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6 hover:border-primary-300 hover:shadow-md transition-all">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                {ConnectorIcons.quantive}
                <div>
                  <h3 className="font-semibold text-[var(--text-pri)]">Quantive</h3>
                  <p className="text-sm text-[var(--text-ter)]">Link items to OKRs</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {getConnectorBadge('quantive')}
                <button
                  onClick={() => handleToggleEnabled('quantive')}
                  disabled={enablingType === 'quantive'}
                  title={isConnectorEnabled('quantive') ? 'Disable for workspace' : 'Enable for workspace'}
                  className={`relative w-9 h-5 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 disabled:opacity-50 ${isConnectorEnabled('quantive') ? 'bg-primary-600' : 'bg-[var(--rule-bright)]'}`}
                  role="switch"
                  aria-checked={isConnectorEnabled('quantive')}
                >
                  <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-[var(--bg-raised)] rounded-full shadow transition-transform ${isConnectorEnabled('quantive') ? 'translate-x-4' : 'translate-x-0'}`} />
                </button>
              </div>
            </div>

            <div className="mt-4 flex items-center gap-2 text-sm text-[var(--text-ter)]">
              <RefreshCcw className="h-4 w-4" />
              <span>Bidirectional</span>
            </div>

            <p className="mt-3 text-sm text-[var(--text-sec)]">
              Link priorities.ai items directly to Quantive Objectives and Key Results.
              Surface strategy alignment on every item and drive OKR progress from your backlog.
            </p>

            <div className="mt-4 flex gap-2">
              {getConnectionForType('quantive') ? (
                <>
                  <button
                    onClick={() => handleConfigure('quantive')}
                    className="flex items-center gap-2 px-3 py-2 text-sm bg-[var(--hover-tint)] hover:bg-[var(--hover-tint)] transition-colors"
                  >
                    <Settings className="h-4 w-4" />
                    Configure
                  </button>
                  <button
                    onClick={() => {
                      const conn = getConnectionForType('quantive');
                      if (conn) handleDisconnect(conn.id);
                    }}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-[var(--coral)] hover:bg-[var(--coral-dim)] transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                    Disconnect
                  </button>
                </>
              ) : (
                <button
                  onClick={() => handleConnect('quantive')}
                  className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white hover:bg-primary-700 transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  Connect Quantive
                </button>
              )}
            </div>
          </div>

          {/* Linear Card */}
          <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6 hover:border-primary-300 hover:shadow-md transition-all">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                {ConnectorIcons.linear}
                <div>
                  <h3 className="font-semibold text-[var(--text-pri)]">Linear</h3>
                  <p className="text-sm text-[var(--text-ter)]">Import issues, export priorities</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {getConnectorBadge('linear')}
                <button
                  onClick={() => handleToggleEnabled('linear')}
                  disabled={enablingType === 'linear'}
                  title={isConnectorEnabled('linear') ? 'Disable for workspace' : 'Enable for workspace'}
                  className={`relative w-9 h-5 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 disabled:opacity-50 ${isConnectorEnabled('linear') ? 'bg-primary-600' : 'bg-[var(--rule-bright)]'}`}
                  role="switch"
                  aria-checked={isConnectorEnabled('linear')}
                >
                  <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-[var(--bg-raised)] rounded-full shadow transition-transform ${isConnectorEnabled('linear') ? 'translate-x-4' : 'translate-x-0'}`} />
                </button>
              </div>
            </div>

            <div className="mt-4 flex items-center gap-2 text-sm text-[var(--text-ter)]">
              <RefreshCcw className="h-4 w-4" />
              <span>Import + Export</span>
            </div>

            <p className="mt-3 text-sm text-[var(--text-sec)]">
              Import Linear issues into prioritization and export ranked priorities back to teams. OAuth preferred; personal API key available as a fallback.
            </p>

            <div className="mt-4 flex gap-2">
              {getConnectionForType('linear') ? (
                <>
                  <button
                    onClick={() => handleConfigure('linear')}
                    className="flex items-center gap-2 px-3 py-2 text-sm bg-[var(--hover-tint)] hover:bg-[var(--hover-tint)] transition-colors"
                  >
                    <Settings className="h-4 w-4" />
                    Configure
                  </button>
                  <button
                    onClick={() => {
                      const conn = getConnectionForType('linear');
                      if (conn) handleDisconnect(conn.id);
                    }}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-[var(--coral)] hover:bg-[var(--coral-dim)] transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                    Disconnect
                  </button>
                </>
              ) : (
                <button
                  onClick={() => handleConnect('linear')}
                  className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white hover:bg-primary-700 transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  Connect Linear
                </button>
              )}
            </div>
          </div>

          </div>
        </div>
        <div>
          <h2 className="text-lg font-semibold text-[var(--text-pri)] mb-1">Customer & Market Signal</h2>
          <p className="text-sm text-[var(--text-ter)] mb-4">Surface what your customers are asking for as weighted demand signals at prioritization time.</p>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">

          {/* Intercom Card */}
          <div id="connector-intercom" className="scroll-mt-6 bg-[var(--bg-raised)] border border-[var(--rule)] p-6 hover:border-primary-300 hover:shadow-md transition-all">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                {ConnectorIcons.intercom}
                <div>
                  <h3 className="font-semibold text-[var(--text-pri)]">Intercom</h3>
                  <p className="text-sm text-[var(--text-ter)]">Read support conversations</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {getConnectorBadge('intercom')}
                <button
                  onClick={() => handleToggleEnabled('intercom')}
                  disabled={enablingType === 'intercom'}
                  title={isConnectorEnabled('intercom') ? 'Disable for workspace' : 'Enable for workspace'}
                  className={`relative w-9 h-5 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 disabled:opacity-50 ${isConnectorEnabled('intercom') ? 'bg-primary-600' : 'bg-[var(--rule-bright)]'}`}
                  role="switch"
                  aria-checked={isConnectorEnabled('intercom')}
                >
                  <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-[var(--bg-raised)] rounded-full shadow transition-transform ${isConnectorEnabled('intercom') ? 'translate-x-4' : 'translate-x-0'}`} />
                </button>
              </div>
            </div>

            <div className="mt-4 flex items-center gap-2 text-sm text-[var(--text-ter)]">
              <ArrowDownToLine className="h-4 w-4" />
              <span>Import</span>
            </div>

            <p className="mt-3 text-sm text-[var(--text-sec)]">
              Read Intercom conversations so agents can surface support themes, CSAT signals, and
              feature requests as candidate backlog items and weighted demand at prioritization time.
            </p>

            <div className="mt-4 flex gap-2">
              {getConnectionForType('intercom') ? (
                <>
                  <button
                    onClick={() => handleConfigure('intercom')}
                    className="flex items-center gap-2 px-3 py-2 text-sm bg-[var(--hover-tint)] hover:bg-[var(--hover-tint)] transition-colors"
                  >
                    <Settings className="h-4 w-4" />
                    Configure
                  </button>
                  <button
                    onClick={() => {
                      const conn = getConnectionForType('intercom');
                      if (conn) handleDisconnect(conn.id);
                    }}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-[var(--coral)] hover:bg-[var(--coral-dim)] transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                    Disconnect
                  </button>
                </>
              ) : (
                <button
                  onClick={() => handleConnect('intercom')}
                  className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white hover:bg-primary-700 transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  Connect Intercom
                </button>
              )}
            </div>
          </div>


          {/* Zendesk Card */}
          <div id="connector-zendesk" className="scroll-mt-6 bg-[var(--bg-raised)] border border-[var(--rule)] p-6 hover:border-primary-300 hover:shadow-md transition-all">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                {ConnectorIcons.zendesk}
                <div>
                  <h3 className="font-semibold text-[var(--text-pri)]">Zendesk</h3>
                  <p className="text-sm text-[var(--text-ter)]">Read support tickets</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {getConnectorBadge('zendesk')}
                <button
                  onClick={() => handleToggleEnabled('zendesk')}
                  disabled={enablingType === 'zendesk'}
                  title={isConnectorEnabled('zendesk') ? 'Disable for workspace' : 'Enable for workspace'}
                  className={`relative w-9 h-5 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 disabled:opacity-50 ${isConnectorEnabled('zendesk') ? 'bg-primary-600' : 'bg-[var(--rule-bright)]'}`}
                  role="switch"
                  aria-checked={isConnectorEnabled('zendesk')}
                >
                  <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-[var(--bg-raised)] rounded-full shadow transition-transform ${isConnectorEnabled('zendesk') ? 'translate-x-4' : 'translate-x-0'}`} />
                </button>
              </div>
            </div>

            <div className="mt-4 flex items-center gap-2 text-sm text-[var(--text-ter)]">
              <ArrowDownToLine className="h-4 w-4" />
              <span>Import</span>
            </div>

            <p className="mt-3 text-sm text-[var(--text-sec)]">
              Read Zendesk Support tickets so agents can turn ticket volume, tags, and CSAT trends
              into demand signals and candidate items — what your support queue means for your backlog.
            </p>

            <div className="mt-4 flex gap-2">
              {getConnectionForType('zendesk') ? (
                <>
                  <button
                    onClick={() => handleConfigure('zendesk')}
                    className="flex items-center gap-2 px-3 py-2 text-sm bg-[var(--hover-tint)] hover:bg-[var(--hover-tint)] transition-colors"
                  >
                    <Settings className="h-4 w-4" />
                    Configure
                  </button>
                  <button
                    onClick={() => {
                      const conn = getConnectionForType('zendesk');
                      if (conn) handleDisconnect(conn.id);
                    }}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-[var(--coral)] hover:bg-[var(--coral-dim)] transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                    Disconnect
                  </button>
                </>
              ) : (
                <button
                  onClick={() => handleConnect('zendesk')}
                  className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white hover:bg-primary-700 transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  Connect Zendesk
                </button>
              )}
            </div>
          </div>


          {/* HubSpot Card */}
          <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6 hover:border-primary-300 hover:shadow-md transition-all">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                {ConnectorIcons.hubspot}
                <div>
                  <h3 className="font-semibold text-[var(--text-pri)]">HubSpot</h3>
                  <p className="text-sm text-[var(--text-ter)]">Read CRM deals &amp; pipeline weight</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {getConnectorBadge('hubspot')}
                <button
                  onClick={() => handleToggleEnabled('hubspot')}
                  disabled={enablingType === 'hubspot'}
                  title={isConnectorEnabled('hubspot') ? 'Disable for workspace' : 'Enable for workspace'}
                  className={`relative w-9 h-5 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 disabled:opacity-50 ${isConnectorEnabled('hubspot') ? 'bg-primary-600' : 'bg-[var(--rule-bright)]'}`}
                  role="switch"
                  aria-checked={isConnectorEnabled('hubspot')}
                >
                  <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-[var(--bg-raised)] rounded-full shadow transition-transform ${isConnectorEnabled('hubspot') ? 'translate-x-4' : 'translate-x-0'}`} />
                </button>
              </div>
            </div>

            <div className="mt-4 flex items-center gap-2 text-sm text-[var(--text-ter)]">
              <ArrowDownToLine className="h-4 w-4" />
              <span>Import</span>
            </div>

            <p className="mt-3 text-sm text-[var(--text-sec)]">
              Read HubSpot CRM deals so agents can map deal stages and weighted pipeline value to
              backlog items — understand what sales is promising and align priorities accordingly.
            </p>

            <div className="mt-4 flex gap-2">
              {getConnectionForType('hubspot') ? (
                <>
                  <button
                    onClick={() => handleConfigure('hubspot')}
                    className="flex items-center gap-2 px-3 py-2 text-sm bg-[var(--hover-tint)] hover:bg-[var(--hover-tint)] transition-colors"
                  >
                    <Settings className="h-4 w-4" />
                    Configure
                  </button>
                  <button
                    onClick={() => {
                      const conn = getConnectionForType('hubspot');
                      if (conn) handleDisconnect(conn.id);
                    }}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-[var(--coral)] hover:bg-[var(--coral-dim)] transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                    Disconnect
                  </button>
                </>
              ) : (
                <button
                  onClick={() => handleConnect('hubspot')}
                  className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white hover:bg-primary-700 transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  Connect HubSpot
                </button>
              )}
            </div>
          </div>


          {/* Salesforce Card */}
          <div id="connector-salesforce" className="scroll-mt-6 bg-[var(--bg-raised)] border border-[var(--rule)] p-6 hover:border-primary-300 hover:shadow-md transition-all">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                {ConnectorIcons.salesforce}
                <div>
                  <h3 className="font-semibold text-[var(--text-pri)]">Salesforce</h3>
                  <p className="text-sm text-[var(--text-ter)]">Read CRM opportunities</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {getConnectorBadge('salesforce')}
                <button
                  onClick={() => handleToggleEnabled('salesforce')}
                  disabled={enablingType === 'salesforce'}
                  title={isConnectorEnabled('salesforce') ? 'Disable for workspace' : 'Enable for workspace'}
                  className={`relative w-9 h-5 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 disabled:opacity-50 ${isConnectorEnabled('salesforce') ? 'bg-primary-600' : 'bg-[var(--rule-bright)]'}`}
                  role="switch"
                  aria-checked={isConnectorEnabled('salesforce')}
                >
                  <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-[var(--bg-raised)] rounded-full shadow transition-transform ${isConnectorEnabled('salesforce') ? 'translate-x-4' : 'translate-x-0'}`} />
                </button>
              </div>
            </div>

            <div className="mt-4 flex items-center gap-2 text-sm text-[var(--text-ter)]">
              <ArrowDownToLine className="h-4 w-4" />
              <span>Import</span>
            </div>

            <p className="mt-3 text-sm text-[var(--text-sec)]">
              Read Salesforce opportunities so agents can surface revenue-at-risk and customer feature
              requests as weighted demand signals and candidate items — see which items block deals or renewals.
            </p>

            <div className="mt-4 flex gap-2">
              {getConnectionForType('salesforce') ? (
                <>
                  <button
                    onClick={() => handleConfigure('salesforce')}
                    className="flex items-center gap-2 px-3 py-2 text-sm bg-[var(--hover-tint)] hover:bg-[var(--hover-tint)] transition-colors"
                  >
                    <Settings className="h-4 w-4" />
                    Configure
                  </button>
                  <button
                    onClick={() => {
                      const conn = getConnectionForType('salesforce');
                      if (conn) handleDisconnect(conn.id);
                    }}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-[var(--coral)] hover:bg-[var(--coral-dim)] transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                    Disconnect
                  </button>
                </>
              ) : (
                <button
                  onClick={() => handleConnect('salesforce')}
                  className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white hover:bg-primary-700 transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  Connect Salesforce
                </button>
              )}
            </div>
          </div>

          </div>
        </div>
        <div>
          <h2 className="text-lg font-semibold text-[var(--text-pri)] mb-1">Collaboration & Scheduling</h2>
          <p className="text-sm text-[var(--text-ter)] mb-4">Notify teams, schedule prioritization sessions, and ingest meeting transcripts and chat as backlog evidence.</p>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">

          {/* Slack Card */}
          <div id="connector-slack" className="scroll-mt-6 bg-[var(--bg-raised)] border border-[var(--rule)] p-6 hover:border-primary-300 hover:shadow-md transition-all">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                {ConnectorIcons.slack}
                <div>
                  <h3 className="font-semibold text-[var(--text-pri)]">Slack</h3>
                  <p className="text-sm text-[var(--text-ter)]">Sync ideas & comments</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {getConnectorBadge('slack')}
                <button
                  onClick={() => handleToggleEnabled('slack')}
                  disabled={enablingType === 'slack'}
                  title={isConnectorEnabled('slack') ? 'Disable for workspace' : 'Enable for workspace'}
                  className={`relative w-9 h-5 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 disabled:opacity-50 ${isConnectorEnabled('slack') ? 'bg-primary-600' : 'bg-[var(--rule-bright)]'}`}
                  role="switch"
                  aria-checked={isConnectorEnabled('slack')}
                >
                  <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-[var(--bg-raised)] rounded-full shadow transition-transform ${isConnectorEnabled('slack') ? 'translate-x-4' : 'translate-x-0'}`} />
                </button>
              </div>
            </div>
            
            <div className="mt-4 flex items-center gap-2 text-sm text-[var(--text-ter)]">
              <RefreshCcw className="h-4 w-4" />
              <span>Bidirectional</span>
            </div>
            
            <p className="mt-3 text-sm text-[var(--text-sec)]">
              Workspace Slack install for ambient digests and shared delivery.
              {slackNotifyChannelName
                ? ` Workspace default channel: #${slackNotifyChannelName}.`
                : getConnectionForType('slack')
                  ? ' Set a workspace default channel under Configure (ambient).'
                  : ''}
              {' '}Personal task notify channels live in{' '}
              <Link to="/prioritization/settings/my-connectors" className="text-primary-600 hover:underline">
                My Connectors
              </Link>
              .
            </p>
            
            <div className="mt-4 flex gap-2">
              {getConnectionForType('slack') ? (
                <>
                  <button
                    onClick={() => handleConfigure('slack')}
                    className="flex items-center gap-2 px-3 py-2 text-sm bg-[var(--hover-tint)] hover:bg-[var(--hover-tint)] transition-colors"
                  >
                    <Settings className="h-4 w-4" />
                    Configure
                  </button>
                  <button
                    onClick={() => {
                      const conn = getConnectionForType('slack');
                      if (conn) handleDisconnect(conn.id);
                    }}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-[var(--coral)] hover:bg-[var(--coral-dim)] transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                    Disconnect
                  </button>
                </>
              ) : (
                <button
                  onClick={() => handleConnect('slack')}
                  className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white hover:bg-primary-700 transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  Connect Slack
                </button>
              )}
            </div>
          </div>


          {/* Google Calendar Card */}
          <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6 hover:border-primary-300 hover:shadow-md transition-all">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                {ConnectorIcons['google-calendar']}
                <div>
                  <h3 className="font-semibold text-[var(--text-pri)]">Google Calendar</h3>
                  <p className="text-sm text-[var(--text-ter)]">Schedule activities</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {getConnectorBadge('google-calendar')}
                <button
                  onClick={() => handleToggleEnabled('google-calendar')}
                  disabled={enablingType === 'google-calendar'}
                  title={isConnectorEnabled('google-calendar') ? 'Disable for workspace' : 'Enable for workspace'}
                  className={`relative w-9 h-5 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 disabled:opacity-50 ${isConnectorEnabled('google-calendar') ? 'bg-primary-600' : 'bg-[var(--rule-bright)]'}`}
                  role="switch"
                  aria-checked={isConnectorEnabled('google-calendar')}
                >
                  <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-[var(--bg-raised)] rounded-full shadow transition-transform ${isConnectorEnabled('google-calendar') ? 'translate-x-4' : 'translate-x-0'}`} />
                </button>
              </div>
            </div>
            
            <div className="mt-4 flex items-center gap-2 text-sm text-[var(--text-ter)]">
              <ArrowUpFromLine className="h-4 w-4" />
              <span>Export only</span>
            </div>
            
            <p className="mt-3 text-sm text-[var(--text-sec)]">
              Automatically create calendar invites when participants are assigned to 
              prioritization activities. Sends reminders and meeting links.
            </p>
            
            <div className="mt-4 flex gap-2">
              {getConnectionForType('google-calendar') ? (
                <>
                  <button
                    onClick={() => handleConfigure('google-calendar')}
                    className="flex items-center gap-2 px-3 py-2 text-sm bg-[var(--hover-tint)] hover:bg-[var(--hover-tint)] transition-colors"
                  >
                    <Settings className="h-4 w-4" />
                    Configure
                  </button>
                  <button
                    onClick={() => {
                      const conn = getConnectionForType('google-calendar');
                      if (conn) handleDisconnect(conn.id);
                    }}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-[var(--coral)] hover:bg-[var(--coral-dim)] transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                    Disconnect
                  </button>
                </>
              ) : (
                <button
                  onClick={() => handleConnect('google-calendar')}
                  className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white hover:bg-primary-700 transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  Connect Google
                </button>
              )}
            </div>
          </div>


          {/* Zoom Card */}
          <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6 hover:border-primary-300 hover:shadow-md transition-all">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                {ConnectorIcons.zoom}
                <div>
                  <h3 className="font-semibold text-[var(--text-pri)]">Zoom</h3>
                  <p className="text-sm text-[var(--text-ter)]">Create video meetings</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {getConnectorBadge('zoom')}
                <button
                  onClick={() => handleToggleEnabled('zoom')}
                  disabled={enablingType === 'zoom'}
                  title={isConnectorEnabled('zoom') ? 'Disable for workspace' : 'Enable for workspace'}
                  className={`relative w-9 h-5 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 disabled:opacity-50 ${isConnectorEnabled('zoom') ? 'bg-primary-600' : 'bg-[var(--rule-bright)]'}`}
                  role="switch"
                  aria-checked={isConnectorEnabled('zoom')}
                >
                  <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-[var(--bg-raised)] rounded-full shadow transition-transform ${isConnectorEnabled('zoom') ? 'translate-x-4' : 'translate-x-0'}`} />
                </button>
              </div>
            </div>

            <div className="mt-4 flex items-center gap-2 text-sm text-[var(--text-ter)]">
              <ArrowUpFromLine className="h-4 w-4" />
              <span>Export only</span>
            </div>

            <p className="mt-3 text-sm text-[var(--text-sec)]">
              Automatically create Zoom meetings when participants are assigned to 
              prioritization sessions. Sends join links directly to attendees.
            </p>

            <div className="mt-4 flex gap-2">
              {getConnectionForType('zoom') ? (
                <>
                  <button
                    onClick={() => handleConfigure('zoom')}
                    className="flex items-center gap-2 px-3 py-2 text-sm bg-[var(--hover-tint)] hover:bg-[var(--hover-tint)] transition-colors"
                  >
                    <Settings className="h-4 w-4" />
                    Configure
                  </button>
                  <button
                    onClick={() => {
                      const conn = getConnectionForType('zoom');
                      if (conn) handleDisconnect(conn.id);
                    }}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-[var(--coral)] hover:bg-[var(--coral-dim)] transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                    Disconnect
                  </button>
                </>
              ) : (
                <button
                  onClick={() => handleConnect('zoom')}
                  className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white hover:bg-primary-700 transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  Connect Zoom
                </button>
              )}
            </div>
          </div>


          {/* Google Meet Card */}
          <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6 hover:border-primary-300 hover:shadow-md transition-all">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                {ConnectorIcons['google-meet']}
                <div>
                  <h3 className="font-semibold text-[var(--text-pri)]">Google Meet</h3>
                  <p className="text-sm text-[var(--text-ter)]">Generate Meet video links</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {getConnectorBadge('google-meet')}
                <button
                  onClick={() => handleToggleEnabled('google-meet')}
                  disabled={enablingType === 'google-meet'}
                  title={isConnectorEnabled('google-meet') ? 'Disable for workspace' : 'Enable for workspace'}
                  className={`relative w-9 h-5 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 disabled:opacity-50 ${isConnectorEnabled('google-meet') ? 'bg-primary-600' : 'bg-[var(--rule-bright)]'}`}
                  role="switch"
                  aria-checked={isConnectorEnabled('google-meet')}
                >
                  <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-[var(--bg-raised)] rounded-full shadow transition-transform ${isConnectorEnabled('google-meet') ? 'translate-x-4' : 'translate-x-0'}`} />
                </button>
              </div>
            </div>

            <div className="mt-4 flex items-center gap-2 text-sm text-[var(--text-ter)]">
              <ArrowUpFromLine className="h-4 w-4" />
              <span>Export only</span>
            </div>

            <p className="mt-3 text-sm text-[var(--text-sec)]">
              Generate Google Meet video links for prioritization sessions via Google Calendar.
              Attendees receive calendar invites with Meet links automatically.
            </p>

            <div className="mt-4 flex gap-2">
              {getConnectionForType('google-meet') ? (
                <>
                  <button
                    onClick={() => handleConfigure('google-meet')}
                    className="flex items-center gap-2 px-3 py-2 text-sm bg-[var(--hover-tint)] hover:bg-[var(--hover-tint)] transition-colors"
                  >
                    <Settings className="h-4 w-4" />
                    Configure
                  </button>
                  <button
                    onClick={() => {
                      const conn = getConnectionForType('google-meet');
                      if (conn) handleDisconnect(conn.id);
                    }}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-[var(--coral)] hover:bg-[var(--coral-dim)] transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                    Disconnect
                  </button>
                </>
              ) : (
                <button
                  onClick={() => handleConnect('google-meet')}
                  className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white hover:bg-primary-700 transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  Connect Google Meet
                </button>
              )}
            </div>
          </div>

          </div>
        </div>
        <div>
          <h2 className="text-lg font-semibold text-[var(--text-pri)] mb-1">Knowledge & Docs</h2>
          <p className="text-sm text-[var(--text-ter)] mb-4">Connect content repositories as AI data sources — keep specs and documents available alongside priorities.</p>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">

          {/* Dropbox Card (content data source for AI) */}
          <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6 hover:border-primary-300 hover:shadow-md transition-all">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                {ConnectorIcons.dropbox}
                <div>
                  <h3 className="font-semibold text-[var(--text-pri)]">Dropbox</h3>
                  <p className="text-sm text-[var(--text-ter)]">Content data source for AI</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {getConnectorBadge('dropbox')}
                <button
                  onClick={() => handleToggleEnabled('dropbox')}
                  disabled={enablingType === 'dropbox'}
                  title={isConnectorEnabled('dropbox') ? 'Disable for workspace' : 'Enable for workspace'}
                  className={`relative w-9 h-5 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 disabled:opacity-50 ${isConnectorEnabled('dropbox') ? 'bg-primary-600' : 'bg-[var(--rule-bright)]'}`}
                  role="switch"
                  aria-checked={isConnectorEnabled('dropbox')}
                >
                  <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-[var(--bg-raised)] rounded-full shadow transition-transform ${isConnectorEnabled('dropbox') ? 'translate-x-4' : 'translate-x-0'}`} />
                </button>
              </div>
            </div>

            <div className="mt-4 flex items-center gap-2 text-sm text-[var(--text-ter)]">
              <ArrowDownToLine className="h-4 w-4" />
              <span>Read only</span>
            </div>

            <p className="mt-3 text-sm text-[var(--text-sec)]">
              Connect Dropbox as a content data source so AI agents can list, search, and read
              the files your team already stores there. Read-only — nothing is modified.
            </p>

            <p className="mt-2 text-xs text-[var(--text-ter)]">
              Where it is read: by agents through the Priorities MCP server or the
              knowledge-sources API, with an API key that holds knowledge:read. Nothing
              inside the app reads it yet.
            </p>

            <div className="mt-4 flex gap-2">
              {getConnectionForType('dropbox') ? (
                <button
                  onClick={() => {
                    const conn = getConnectionForType('dropbox');
                    if (conn) handleDisconnect(conn.id);
                  }}
                  className="flex items-center gap-2 px-3 py-2 text-sm text-[var(--coral)] hover:bg-[var(--coral-dim)] transition-colors"
                >
                  <Trash2 className="h-4 w-4" />
                  Disconnect
                </button>
              ) : (
                <button
                  onClick={() => handleConnect('dropbox')}
                  className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white hover:bg-primary-700 transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  Connect Dropbox
                </button>
              )}
            </div>
          </div>


          {/* Google Docs Card (content data source for AI) */}
          <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6 hover:border-primary-300 hover:shadow-md transition-all">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                {ConnectorIcons['google-docs']}
                <div>
                  <h3 className="font-semibold text-[var(--text-pri)]">Google Docs</h3>
                  <p className="text-sm text-[var(--text-ter)]">Content data source for AI</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {getConnectorBadge('google-docs')}
                <button
                  onClick={() => handleToggleEnabled('google-docs')}
                  disabled={enablingType === 'google-docs'}
                  title={isConnectorEnabled('google-docs') ? 'Disable for workspace' : 'Enable for workspace'}
                  className={`relative w-9 h-5 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 disabled:opacity-50 ${isConnectorEnabled('google-docs') ? 'bg-primary-600' : 'bg-[var(--rule-bright)]'}`}
                  role="switch"
                  aria-checked={isConnectorEnabled('google-docs')}
                >
                  <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-[var(--bg-raised)] rounded-full shadow transition-transform ${isConnectorEnabled('google-docs') ? 'translate-x-4' : 'translate-x-0'}`} />
                </button>
              </div>
            </div>

            <div className="mt-4 flex items-center gap-2 text-sm text-[var(--text-ter)]">
              <ArrowDownToLine className="h-4 w-4" />
              <span>Read only</span>
            </div>

            <p className="mt-3 text-sm text-[var(--text-sec)]">
              Connect Google Docs as a content data source so AI agents can list, search, and read
              your specs, briefs, and decision docs. Read-only — nothing is modified.
            </p>

            <p className="mt-2 text-xs text-[var(--text-ter)]">
              Where it is read: by agents through the Priorities MCP server or the
              knowledge-sources API, with an API key that holds knowledge:read. Nothing
              inside the app reads it yet.
            </p>

            <div className="mt-4 flex gap-2">
              {getConnectionForType('google-docs') ? (
                <button
                  onClick={() => {
                    const conn = getConnectionForType('google-docs');
                    if (conn) handleDisconnect(conn.id);
                  }}
                  className="flex items-center gap-2 px-3 py-2 text-sm text-[var(--coral)] hover:bg-[var(--coral-dim)] transition-colors"
                >
                  <Trash2 className="h-4 w-4" />
                  Disconnect
                </button>
              ) : (
                <button
                  onClick={() => handleConnect('google-docs')}
                  className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white hover:bg-primary-700 transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  Connect Google Docs
                </button>
              )}
            </div>
          </div>

          {/* Notion Card (bidirectional work connector + AI content source) */}
          <div id="connector-notion" className="scroll-mt-6 bg-[var(--bg-raised)] border border-[var(--rule)] p-6 hover:border-primary-300 hover:shadow-md transition-all">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                {ConnectorIcons.notion}
                <div>
                  <h3 className="font-semibold text-[var(--text-pri)]">Notion</h3>
                  <p className="text-sm text-[var(--text-ter)]">Databases, pages & AI content source</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {getConnectorBadge('notion')}
                <button
                  onClick={() => handleToggleEnabled('notion')}
                  disabled={enablingType === 'notion'}
                  title={isConnectorEnabled('notion') ? 'Disable for workspace' : 'Enable for workspace'}
                  className={`relative w-9 h-5 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 disabled:opacity-50 ${isConnectorEnabled('notion') ? 'bg-primary-600' : 'bg-[var(--rule-bright)]'}`}
                  role="switch"
                  aria-checked={isConnectorEnabled('notion')}
                >
                  <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-[var(--bg-raised)] rounded-full shadow transition-transform ${isConnectorEnabled('notion') ? 'translate-x-4' : 'translate-x-0'}`} />
                </button>
              </div>
            </div>

            <div className="mt-4 flex items-center gap-2 text-sm text-[var(--text-ter)]">
              <ArrowDownToLine className="h-4 w-4" />
              <ArrowUpFromLine className="h-4 w-4" />
              <span>Import & export</span>
            </div>

            <p className="mt-3 text-sm text-[var(--text-sec)]">
              Import a Notion database as candidate items, export priority lists back as pages, and
              read connected pages as an AI knowledge source. Connect with OAuth, or paste an
              internal integration token for local single-user setups.
            </p>

            <div className="mt-4 flex gap-2">
              {getConnectionForType('notion') ? (
                <>
                  <button
                    onClick={() => handleConfigure('notion')}
                    className="flex items-center gap-2 px-3 py-2 text-sm bg-[var(--hover-tint)] hover:bg-[var(--hover-tint)] transition-colors"
                  >
                    <Settings className="h-4 w-4" />
                    Configure
                  </button>
                  <button
                    onClick={() => {
                      const conn = getConnectionForType('notion');
                      if (conn) handleDisconnect(conn.id);
                    }}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-[var(--coral)] hover:bg-[var(--coral-dim)] transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                    Disconnect
                  </button>
                </>
              ) : (
                <button
                  onClick={() => handleConnect('notion')}
                  className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white hover:bg-primary-700 transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  Connect Notion
                </button>
              )}
            </div>
          </div>


          </div>
        </div>
        <div>
          <h2 className="text-lg font-semibold text-[var(--text-pri)] mb-1">Data & Engineering</h2>
          <p className="text-sm text-[var(--text-ter)] mb-4">Enrich items with observability metrics and warehouse data — let quantitative signals inform what gets prioritized next.</p>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">

          {/* Datadog Card */}
          <div id="connector-datadog" className="scroll-mt-6 bg-[var(--bg-raised)] border border-[var(--rule)] p-6 hover:border-primary-300 hover:shadow-md transition-all">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                {ConnectorIcons.datadog}
                <div>
                  <h3 className="font-semibold text-[var(--text-pri)]">Datadog</h3>
                  <p className="text-sm text-[var(--text-ter)]">Reliability evidence on items</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {getConnectorBadge('datadog')}
                <button
                  onClick={() => handleToggleEnabled('datadog')}
                  disabled={enablingType === 'datadog'}
                  title={isConnectorEnabled('datadog') ? 'Disable for workspace' : 'Enable for workspace'}
                  className={`relative w-9 h-5 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 disabled:opacity-50 ${isConnectorEnabled('datadog') ? 'bg-primary-600' : 'bg-[var(--rule-bright)]'}`}
                  role="switch"
                  aria-checked={isConnectorEnabled('datadog')}
                >
                  <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-[var(--bg-raised)] rounded-full shadow transition-transform ${isConnectorEnabled('datadog') ? 'translate-x-4' : 'translate-x-0'}`} />
                </button>
              </div>
            </div>

            <div className="mt-4 flex items-center gap-2 text-sm text-[var(--text-ter)]">
              <ArrowDownToLine className="h-4 w-4" />
              <span>Import</span>
            </div>

            <p className="mt-3 text-sm text-[var(--text-sec)]">
              Pull SLO status, incident counts, and monitor alerting from Datadog as evidence on
              reliability items. Let observability data inform what engineering prioritizes next.
            </p>

            <div className="mt-4 flex gap-2">
              {getConnectionForType('datadog') ? (
                <>
                  <button
                    onClick={() => handleConfigure('datadog')}
                    className="flex items-center gap-2 px-3 py-2 text-sm bg-[var(--hover-tint)] hover:bg-[var(--hover-tint)] transition-colors"
                  >
                    <Settings className="h-4 w-4" />
                    Configure
                  </button>
                  <button
                    onClick={() => {
                      const conn = getConnectionForType('datadog');
                      if (conn) handleDisconnect(conn.id);
                    }}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-[var(--coral)] hover:bg-[var(--coral-dim)] transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                    Disconnect
                  </button>
                </>
              ) : (
                <button
                  onClick={() => handleConnect('datadog')}
                  className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white hover:bg-primary-700 transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  Connect Datadog
                </button>
              )}
            </div>
          </div>


          {/* Snowflake Card */}
          <div id="connector-snowflake" className="scroll-mt-6 bg-[var(--bg-raised)] border border-[var(--rule)] p-6 hover:border-primary-300 hover:shadow-md transition-all">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                {ConnectorIcons.snowflake}
                <div>
                  <h3 className="font-semibold text-[var(--text-pri)]">Snowflake</h3>
                  <p className="text-sm text-[var(--text-ter)]">Enrich items with warehouse data</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {getConnectorBadge('snowflake')}
                <button
                  onClick={() => handleToggleEnabled('snowflake')}
                  disabled={enablingType === 'snowflake'}
                  title={isConnectorEnabled('snowflake') ? 'Disable for workspace' : 'Enable for workspace'}
                  className={`relative w-9 h-5 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 disabled:opacity-50 ${isConnectorEnabled('snowflake') ? 'bg-primary-600' : 'bg-[var(--rule-bright)]'}`}
                  role="switch"
                  aria-checked={isConnectorEnabled('snowflake')}
                >
                  <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-[var(--bg-raised)] rounded-full shadow transition-transform ${isConnectorEnabled('snowflake') ? 'translate-x-4' : 'translate-x-0'}`} />
                </button>
              </div>
            </div>

            <div className="mt-4 flex items-center gap-2 text-sm text-[var(--text-ter)]">
              <ArrowDownToLine className="h-4 w-4" />
              <span>Import (read-only)</span>
            </div>

            <p className="mt-3 text-sm text-[var(--text-sec)]">
              Run read-only queries against your data warehouse to enrich items with usage metrics,
              revenue attribution, and custom business signals at prioritization time.
            </p>

            <div className="mt-4 flex gap-2">
              {getConnectionForType('snowflake') ? (
                <>
                  <button
                    onClick={() => handleConfigure('snowflake')}
                    className="flex items-center gap-2 px-3 py-2 text-sm bg-[var(--hover-tint)] hover:bg-[var(--hover-tint)] transition-colors"
                  >
                    <Settings className="h-4 w-4" />
                    Configure
                  </button>
                  <button
                    onClick={() => {
                      const conn = getConnectionForType('snowflake');
                      if (conn) handleDisconnect(conn.id);
                    }}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-[var(--coral)] hover:bg-[var(--coral-dim)] transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                    Disconnect
                  </button>
                </>
              ) : (
                <button
                  onClick={() => handleConnect('snowflake')}
                  className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white hover:bg-primary-700 transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  Connect Snowflake
                </button>
              )}
            </div>
          </div>

          </div>
        </div>
      </div>

      {/* CSV — Data Citizenship (not a connectable OAuth connector) */}
      {otherConnectors.length > 0 && (
        <div className="mb-10">
          <h2 className="text-lg font-semibold text-[var(--text-pri)] mb-1">CSV Import / Export</h2>
          <p className="text-sm text-[var(--text-ter)] mb-4">
            Import and export items as a spreadsheet file — set up under Spreadsheet sync, not as a connector login.
          </p>
          <div className="bg-[var(--bg-raised)] border border-[var(--rule)] divide-y">
            {otherConnectors.map(connector => (
              <div key={connector.type} className="p-4 flex items-center justify-between gap-4">
                <div className="flex items-center gap-4 min-w-0">
                  <div className="w-10 h-10 bg-[var(--hover-tint)] flex items-center justify-center flex-shrink-0">
                    <Link2 className="h-5 w-5 text-[var(--text-ter)]" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-medium text-[var(--text-pri)]">{connector.name}</h3>
                    <p className="text-sm text-[var(--text-ter)]">
                      Saved mappings, schedules and Run now are in Settings → Spreadsheet sync.
                    </p>
                  </div>
                </div>
                <Link
                  to="/prioritization/settings/csv-citizenship"
                  className="flex-shrink-0 text-sm font-medium text-primary-600 hover:text-primary-700 whitespace-nowrap"
                >
                  Open Spreadsheet sync →
                </Link>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Engine status moved to Settings → Status */}
      <div className="flex items-center justify-between px-4 py-3 bg-[var(--surface)] border border-[var(--rule)]">
        <div className="flex items-center gap-2">
          {engineOnline === null && <div className="w-2 h-2 bg-[var(--rule-bright)] rounded-full animate-pulse" />}
          {engineOnline === true  && <div className="w-2 h-2 bg-[var(--green)] rounded-full animate-pulse" />}
          {engineOnline === false && <div className="w-2 h-2 bg-[var(--coral)] rounded-full" />}
          <span className="text-xs text-[var(--text-sec)]">
            {engineOnline === null  && 'Checking integration engine…'}
            {engineOnline === true  && 'Integration engine online'}
            {engineOnline === false && 'Integration engine offline'}
          </span>
        </div>
        <Link to="/prioritization/settings/status" className="text-xs text-primary-600 hover:underline flex-shrink-0">
          View full status →
        </Link>
      </div>

      {successMessage && (
        <div className="flex items-center gap-2 px-4 py-3 bg-[var(--green-dim)] border border-[var(--green)] text-sm text-[var(--green)] dark:text-[var(--text-ter)]">
          <Check className="h-4 w-4 flex-shrink-0" />
          <span className="flex-1">{successMessage}</span>
          <button
            type="button"
            onClick={() => setSuccessMessage(null)}
            className="text-[var(--green)] dark:text-[var(--text-ter)] hover:text-[var(--green)] dark:hover:text-[var(--text-ter)] text-xs underline"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* AI provider API key modal */}
      {keyModalProvider && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-[var(--bg-raised)] shadow-xl w-full max-w-md p-6 m-4">
            <div className="flex items-center gap-3 mb-5">
              <div
                className={`flex h-10 w-10 items-center justify-center  ${
                  keyModalProvider === 'openai'
                    ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]'
                    : 'bg-[var(--mustard-dim)] text-[var(--mustard)]'
                }`}
              >
                {keyModalProvider === 'openai' ? <ChatGPTLogo /> : <ClaudeLogo />}
              </div>
              <div>
                <h2 className="text-lg font-semibold text-[var(--text-pri)]">
                  {keyModalProvider === 'openai'
                    ? (hasOpenAIKey ? 'Update OpenAI API Key' : 'Add OpenAI API Key')
                    : (hasAnthropicKey ? 'Update Claude API Key' : 'Add Claude API Key')}
                </h2>
                <p className="text-sm text-[var(--text-ter)]">Kept on our server, encrypted</p>
              </div>
            </div>

            {(keyModalProvider === 'openai' ? openaiKeyPreview : anthropicKeyPreview) && (
              <div className="mb-4 flex items-center gap-2 px-3 py-2 bg-[var(--surface)] text-sm text-[var(--text-sec)]">
                <span className="text-[var(--text-ter)]">Current key:</span>
                <span className="font-mono">
                  {keyModalProvider === 'openai' ? openaiKeyPreview : anthropicKeyPreview}
                </span>
              </div>
            )}

            <div className="space-y-3">
              <label className="block text-sm font-medium text-[var(--text-sec)]">
                New API Key
              </label>
              <input
                type="password"
                value={keyInput}
                onChange={(e) => setKeyInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && void handleSaveKey()}
                placeholder={keyModalProvider === 'openai' ? 'sk-...' : 'sk-ant-...'}
                autoFocus
                className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm"
              />
              <p className="text-xs text-[var(--text-ter)]">
                {keyModalProvider === 'openai' ? (
                  <>
                    Get your key at{' '}
                    <a
                      href="https://platform.openai.com/api-keys"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-600 hover:underline"
                    >
                      platform.openai.com/api-keys
                    </a>
                  </>
                ) : (
                  <>
                    Get your key at{' '}
                    <a
                      href="https://console.anthropic.com/settings/keys"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-600 hover:underline"
                    >
                      console.anthropic.com
                    </a>
                  </>
                )}
                . Your key is kept on our server, encrypted, and used only for your own requests; once saved, only its last four characters are shown.
              </p>
              {keyError && <p className="text-sm text-[var(--coral)]">{keyError}</p>}
            </div>

            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={closeKeyModal}
                className="flex-1 px-4 py-2 border border-[var(--rule)] hover:bg-[var(--surface)] transition-colors text-sm"
              >
                Cancel
              </button>
              {hasSavedAiKey(keyModalProvider) && (
                <button
                  type="button"
                  onClick={() => void handleClearKey()}
                  className="px-4 py-2 text-[var(--coral)] hover:bg-[var(--coral-dim)] border border-[var(--coral)] transition-colors text-sm"
                >
                  Remove key
                </button>
              )}
              <button
                type="button"
                onClick={() => void handleSaveKey()}
                disabled={!keyInput.trim()}
                className="flex-1 px-4 py-2 bg-primary-600 text-white hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                Save key
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Connect Modal */}
      {showConnectModal && selectedConnectorType && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-[var(--bg-raised)] shadow-xl w-full max-w-md p-6 m-4">
            <div className="flex items-center gap-3 mb-6">
              {ConnectorIcons[selectedConnectorType] || (
                <div className="w-8 h-8 bg-[var(--hover-tint)] flex items-center justify-center">
                  <Link2 className="h-4 w-4 text-[var(--text-ter)]" />
                </div>
              )}
              <div>
                <h2 className="text-xl font-semibold">
                  {isConfigureMode ? 'Configure' : 'Connect'}{' '}{
                    ({
                      miro: 'Miro',
                      jira: 'Jira',
                      slack: 'Slack',
                      notion: 'Notion',
                      linear: 'Linear',
                      zoom: 'Zoom',
                      'google-meet': 'Google Meet',
                      figma: 'Figma',
                      dropbox: 'Dropbox',
                      'google-docs': 'Google Docs',
                    } as Partial<Record<ConnectorType, string>>)[selectedConnectorType] || selectedConnectorType
                  }
                </h2>
                <p className="text-sm text-[var(--text-ter)]">
                  {
                    ({
                      miro: 'Import sticky notes from your boards',
                      jira: 'Export priorities as Jira epics',
                      slack: 'Sync ideas and comments with Slack',
                      notion: 'Import from databases, export as pages',
                      linear: 'Import issues, export priorities to teams',
                      zoom: 'Create Zoom meetings for prioritization sessions',
                      'google-meet': 'Generate Google Meet links via Google Calendar',
                      quantive: 'Link items to Quantive OKRs for strategy alignment',
                      granola: 'Link items to Granola meeting notes for decision traceability',
                      raycast: 'Add items and act on priorities from the Raycast launcher',
                      mcp: 'Expose priorities as an MCP resource for agents and IDEs',
                      figma: 'Import frames as items, export priorities as comments',
                      dropbox: 'Connect Dropbox as a content data source for AI',
                      'google-docs': 'Connect Google Docs as a content data source for AI',
                    } as Partial<Record<ConnectorType, string>>)[selectedConnectorType] || 'Connect this integration'
                  }
                </p>
              </div>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-[var(--coral-dim)] text-[var(--coral)] flex items-center gap-2">
                <AlertCircle className="h-5 w-5" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            {selectedConnectorType === 'miro' ? (
              <div className="space-y-4">
                {isConfigureMode && (connections.find(c => c.type === 'miro')?.config as Record<string,string> | undefined)?.accessToken && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-[var(--green-dim)] border border-[var(--green)] text-sm text-[var(--green)] dark:text-[var(--text-ter)]">
                    <Check className="h-4 w-4 flex-shrink-0" />
                    <span>Access token is saved. Enter a new token below to replace it, or leave blank to keep the current one.</span>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                    {isConfigureMode ? 'New Access Token (optional)' : 'Access Token'}
                  </label>
                  <input
                    type="password"
                    value={miroToken}
                    onChange={(e) => setMiroToken(e.target.value)}
                    placeholder={isConfigureMode ? 'Leave blank to keep current token' : 'Enter your Miro access token'}
                    className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <p className="mt-1 text-xs text-[var(--text-ter)]">
                    Get your token at{' '}
                    <a 
                      href="https://miro.com/app/settings/user-profile/apps" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary-600 hover:underline"
                    >
                      Miro Developer Settings
                    </a>
                  </p>
                </div>
              </div>
            ) : selectedConnectorType === 'jira' ? (
              <div className="space-y-4">
                {isConfigureMode && (connections.find(c => c.type === 'jira')?.config as Record<string,string> | undefined)?.apiToken && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-[var(--green-dim)] border border-[var(--green)] text-sm text-[var(--green)] dark:text-[var(--text-ter)]">
                    <Check className="h-4 w-4 flex-shrink-0" />
                    <span>Credentials are saved. Update any field below, or leave blank to keep existing values.</span>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                    Jira Site URL
                  </label>
                  <input
                    type="url"
                    value={jiraSiteUrl}
                    onChange={(e) => setJiraSiteUrl(e.target.value)}
                    placeholder="https://your-domain.atlassian.net"
                    className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={jiraEmail}
                    onChange={(e) => setJiraEmail(e.target.value)}
                    placeholder="your-email@example.com"
                    className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                    {isConfigureMode ? 'API Token (optional — leave blank to keep current)' : 'API Token'}
                  </label>
                  <input
                    type="password"
                    value={jiraToken}
                    onChange={(e) => setJiraToken(e.target.value)}
                    placeholder={isConfigureMode ? 'Leave blank to keep current token' : 'Enter your Jira API token'}
                    className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <p className="mt-1 text-xs text-[var(--text-ter)]">
                    Get your token at{' '}
                    <a 
                      href="https://id.atlassian.com/manage-profile/security/api-tokens" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary-600 hover:underline"
                    >
                      Atlassian API Tokens
                    </a>
                  </p>
                </div>
              </div>
            ) : selectedConnectorType === 'slack' ? (
              <div className="space-y-4">
                {isConfigureMode && getConnectionForType('slack') ? (
                  <>
                    <div className="flex items-center gap-2 px-3 py-2 bg-[var(--green-dim)] border border-[var(--green)] text-sm text-[var(--green)] dark:text-[var(--text-ter)]">
                      <Check className="h-4 w-4 flex-shrink-0" />
                      <span>
                        Workspace Slack is connected
                        {slackNotifyChannelName ? ` · ambient default → #${slackNotifyChannelName}` : ''}.
                        Set the workspace default channel for Ambient Runtime digests.
                        Personal task channels are in{' '}
                        <Link to="/prioritization/settings/my-connectors" className="underline font-medium">
                          My Connectors
                        </Link>
                        .
                      </span>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                        Workspace default channel (ambient)
                      </label>
                      {slackChannelsLoading ? (
                        <p className="text-sm text-[var(--text-ter)]">Loading channels…</p>
                      ) : (
                        <select
                          value={slackNotifyChannelId}
                          onChange={(e) => setSlackNotifyChannelId(e.target.value)}
                          className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        >
                          <option value="">Select a channel…</option>
                          {slackAvailableChannels.map((ch) => (
                            <option key={ch.id} value={ch.id}>
                              #{ch.name}{ch.isPrivate ? ' (private)' : ''}
                            </option>
                          ))}
                        </select>
                      )}
                      <p className="mt-1 text-xs text-[var(--text-ter)]">
                        Used for Ambient Runtime Slack delivery and as a fallback when a user has no personal channel.
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="p-4 bg-[var(--slate-dim)] border border-[var(--slate)]">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0">
                          {ConnectorIcons.slack}
                        </div>
                        <div>
                          <h4 className="font-medium text-[var(--text-sec)]">Connect with Slack</h4>
                          <p className="text-sm text-[var(--text-sec)] mt-1">
                            You'll be redirected to Slack to authorize Peak Priorities for this workspace.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <h4 className="text-sm font-medium text-[var(--text-sec)]">What this integration does:</h4>
                      <ul className="text-sm text-[var(--text-sec)] space-y-1">
                        <li className="flex items-start gap-2">
                          <Check className="h-4 w-4 text-[var(--green)] mt-0.5 flex-shrink-0" />
                          <span>Posts Ambient Runtime digests to a workspace default channel</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Check className="h-4 w-4 text-[var(--green)] mt-0.5 flex-shrink-0" />
                          <span>Lets people choose personal task channels under My Connectors</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Check className="h-4 w-4 text-[var(--green)] mt-0.5 flex-shrink-0" />
                          <span>Keeps prioritization activity visible where the team already works</span>
                        </li>
                      </ul>
                    </div>
                    <div className="p-3 bg-[var(--mustard-dim)] border border-[var(--mustard)]">
                      <p className="text-sm text-[var(--mustard)]">
                        <strong>Note:</strong> After connecting, use Configure to set the workspace ambient channel.
                        Personal notification channels are managed in My Connectors.
                      </p>
                    </div>
                  </>
                )}
              </div>
            ) : selectedConnectorType === 'notion' ? (
              <div className="space-y-4">
                {isConfigureMode && (connections.find(c => c.type === 'notion')?.config as Record<string,string> | undefined)?.accessToken && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-[var(--green-dim)] border border-[var(--green)] text-sm text-[var(--green)] dark:text-[var(--text-ter)]">
                    <Check className="h-4 w-4 flex-shrink-0" />
                    <span>Notion is connected. Reconnect below to replace the connection, or use the advanced option to swap in an internal integration token.</span>
                  </div>
                )}
                <div className="p-3 bg-[var(--surface)]">
                  <p className="text-sm text-[var(--text-sec)]">
                    Connect with Notion via OAuth — you'll be redirected to pick the pages and
                    databases to share, then returned here. No token to copy. This is the
                    recommended path.
                  </p>
                </div>
                <details className="group">
                  <summary className="text-sm text-[var(--text-sec)] cursor-pointer select-none hover:text-[var(--text-pri)]">
                    Advanced: use an internal integration token (local / single-user)
                  </summary>
                  <div className="mt-3">
                    <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                      Internal Integration Token (optional)
                    </label>
                    <input
                      type="password"
                      value={notionToken}
                      onChange={(e) => setNotionToken(e.target.value)}
                      placeholder={isConfigureMode ? 'Leave blank to keep current connection' : 'ntn_... (or secret_...)'}
                      className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                    <p className="mt-1 text-xs text-[var(--text-ter)]">
                      For a single local user. Create an internal integration at{' '}
                      <a href="https://www.notion.so/my-integrations" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">
                        Notion Integrations
                      </a>
                      , then share the pages/databases with it. Leave blank to connect with OAuth.
                    </p>
                  </div>
                </details>
              </div>
            ) : selectedConnectorType === 'linear' ? (
              <div className="space-y-4">
                {isConfigureMode && (connections.find(c => c.type === 'linear')?.config as Record<string,string> | undefined)?.apiKey && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-[var(--green-dim)] border border-[var(--green)] text-sm text-[var(--green)] dark:text-[var(--text-ter)]">
                    <Check className="h-4 w-4 flex-shrink-0" />
                    <span>An API key is saved. Enter a new key to replace it, or leave blank to keep the current one.</span>
                  </div>
                )}
                <div className="p-3 bg-[var(--surface)]">
                  <p className="text-sm text-[var(--text-sec)]">
                    Connect with Linear via OAuth — you'll be redirected to authorize access, then
                    returned here. No token to copy. This is the recommended path.
                  </p>
                </div>
                <details className="group">
                  <summary className="text-sm text-[var(--text-sec)] cursor-pointer select-none hover:text-[var(--text-pri)]">
                    Advanced: use a personal API key instead
                  </summary>
                  <div className="mt-3">
                    <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                      Personal API Key (optional)
                    </label>
                    <input
                      type="password"
                      value={linearApiKey}
                      onChange={(e) => setLinearApiKey(e.target.value)}
                      placeholder={isConfigureMode ? 'Leave blank to keep current key' : 'lin_api_...'}
                      className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                    <p className="mt-1 text-xs text-[var(--text-ter)]">
                      For single-user setups. Create a key at{' '}
                      <a href="https://linear.app/settings/api" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">
                        Linear Settings → API
                      </a>
                      . Leave blank to connect with OAuth.
                    </p>
                  </div>
                </details>
              </div>
            ) : selectedConnectorType === 'quantive' ? (
              <div className="space-y-4">
                {isConfigureMode && (connections.find(c => c.type === 'quantive')?.config as Record<string,string> | undefined)?.apiKey && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-[var(--green-dim)] border border-[var(--green)] text-sm text-[var(--green)] dark:text-[var(--text-ter)]">
                    <Check className="h-4 w-4 flex-shrink-0" />
                    <span>Credentials are saved. Leave any field blank to keep the current value.</span>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                    {isConfigureMode ? 'API Key (optional)' : 'API Key'}
                  </label>
                  <input
                    type="password"
                    value={quantiveApiKey}
                    onChange={(e) => setQuantiveApiKey(e.target.value)}
                    placeholder={isConfigureMode ? 'Leave blank to keep current key' : 'Enter your Quantive API key'}
                    className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <p className="mt-1 text-xs text-[var(--text-ter)]">
                    Generate a key at{' '}
                    <a href="https://app.quantive.com/results/settings/api" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">
                      Quantive Settings → API
                    </a>
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                    {isConfigureMode ? 'Account ID (optional)' : 'Account ID'}
                  </label>
                  <input
                    type="text"
                    value={quantiveAccountId}
                    onChange={(e) => setQuantiveAccountId(e.target.value)}
                    placeholder="e.g. 5f3c2a1b4d..."
                    className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm"
                  />
                  <p className="mt-1 text-xs text-[var(--text-ter)]">
                    Found in Quantive Settings → Account → Account ID.
                  </p>
                </div>
                <div className="p-3 border border-cyan-200/70 dark:border-cyan-800/40 bg-cyan-50 dark:bg-cyan-950/25">
                  <p className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)]">
                    <strong className="text-[var(--text-pri)] dark:text-[var(--text-pri)]">What this enables:</strong>{' '}
                    Link any priorities.ai item directly to a Quantive Objective or Key Result from the item's Classification tab. Changes to OKR status and progress surface on the item.
                  </p>
                </div>
              </div>
            ) : selectedConnectorType === 'granola' ? (
              <div className="space-y-4">
                {isConfigureMode && (connections.find(c => c.type === 'granola')?.config as Record<string,string> | undefined)?.apiKey && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-[var(--green-dim)] dark:bg-[var(--green)]/30 border border-[var(--green)] dark:border-[var(--green)]/50 text-sm text-[var(--green)] dark:text-[var(--text-ter)]">
                    <Check className="h-4 w-4 flex-shrink-0" />
                    <span>Credentials are saved. Leave blank to keep the current key.</span>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                    {isConfigureMode ? 'API Key (optional)' : 'API Key'}
                  </label>
                  <input
                    type="password"
                    value={granolaApiKey}
                    onChange={(e) => setGranolaApiKey(e.target.value)}
                    placeholder={isConfigureMode ? 'Leave blank to keep current key' : 'grn_...'}
                    className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm"
                  />
                  <p className="mt-1 text-xs text-[var(--text-ter)]">
                    Generate a key at{' '}
                    <a href="https://app.granola.ai/settings/api" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">
                      Granola Settings → API
                    </a>{' '}
                    (Business or Enterprise plan required).
                  </p>
                </div>
                <div className="p-3 border border-[var(--green)]/70 dark:border-[var(--green)]/40 bg-[var(--green-dim)] dark:bg-[var(--green)]/25">
                  <p className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)]">
                    <strong className="text-[var(--text-pri)] dark:text-[var(--text-pri)]">What this enables:</strong>{' '}
                    Link any priorities.ai item to the Granola meeting note where it was discussed or decided, from the item's Classification tab. Trace strategic decisions back to the conversations that created them.
                  </p>
                </div>
              </div>
            ) : selectedConnectorType === 'raycast' ? (
              <div className="space-y-4">
                {isConfigureMode && (connections.find(c => c.type === 'raycast')?.config as Record<string,string> | undefined)?.apiKey && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-[var(--green-dim)] dark:bg-[var(--green)]/30 border border-[var(--green)] dark:border-[var(--green)]/50 text-sm text-[var(--green)] dark:text-[var(--text-ter)]">
                    <Check className="h-4 w-4 flex-shrink-0" />
                    <span>Credentials are saved. Leave blank to keep the current key.</span>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                    {isConfigureMode ? 'API Key (optional)' : 'API Key'}
                  </label>
                  <input
                    type="password"
                    value={raycastApiKey}
                    onChange={(e) => setRaycastApiKey(e.target.value)}
                    placeholder={isConfigureMode ? 'Leave blank to keep current key' : 'pk_live_... or extension key'}
                    className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm"
                  />
                  <p className="mt-1 text-xs text-[var(--text-ter)]">
                    Use a priorities.ai platform API key in the Raycast extension so it can add items, search the backlog, and take action without leaving the launcher.
                  </p>
                </div>
                <div className="p-3 border border-[var(--coral)]/70 dark:border-[var(--coral)]/40 bg-[var(--coral-dim)] dark:bg-[var(--coral)]/25">
                  <p className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)]">
                    <strong className="text-[var(--text-pri)] dark:text-[var(--text-pri)]">What this enables:</strong>{' '}
                    Keyboard-first backlog capture and search from Raycast. Enable the connector for your workspace, then paste the same API key into the Raycast extension preferences.
                  </p>
                </div>
              </div>
            ) : selectedConnectorType === 'mcp' ? (
              <div className="space-y-4">
                {isConfigureMode && (connections.find(c => c.type === 'mcp')?.config as Record<string,string> | undefined)?.apiKey && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-[var(--green-dim)] dark:bg-[var(--green)]/30 border border-[var(--green)] dark:border-[var(--green)]/50 text-sm text-[var(--green)] dark:text-[var(--text-ter)]">
                    <Check className="h-4 w-4 flex-shrink-0" />
                    <span>Credentials are saved. Leave blank to keep the current key.</span>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                    {isConfigureMode ? 'API Key (optional)' : 'API Key'}
                  </label>
                  <input
                    type="password"
                    value={mcpApiKey}
                    onChange={(e) => setMcpApiKey(e.target.value)}
                    placeholder={isConfigureMode ? 'Leave blank to keep current key' : 'pk_live_...'}
                    className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm"
                  />
                  <p className="mt-1 text-xs text-[var(--text-ter)]">
                    Use a priorities.ai platform API key so MCP clients (Cursor, Claude, and other agents) can query and update this workspace's backlog.
                  </p>
                </div>
              </div>
            ) : selectedConnectorType === 'figma' ? (
              <div className="space-y-4">
                {isConfigureMode && (connections.find(c => c.type === 'figma')?.config as Record<string,string> | undefined)?.accessToken && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-[var(--green-dim)] border border-[var(--green)] text-sm text-[var(--green)] dark:text-[var(--text-ter)]">
                    <Check className="h-4 w-4 flex-shrink-0" />
                    <span>Token is saved. Leave blank to keep the current value.</span>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                    {isConfigureMode ? 'Personal Access Token (optional)' : 'Personal Access Token'}
                  </label>
                  <input
                    type="password"
                    value={figmaAccessToken}
                    onChange={(e) => setFigmaAccessToken(e.target.value)}
                    placeholder={isConfigureMode ? 'Leave blank to keep current token' : 'figd_...'}
                    className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm"
                  />
                  <p className="mt-1 text-xs text-[var(--text-ter)]">
                    Generate at{' '}
                    <a href="https://www.figma.com/settings" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">
                      Figma Account Settings → Personal access tokens
                    </a>
                    . Read-only scope is sufficient for import.
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                    Team ID <span className="text-[var(--text-ter)] font-normal">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={figmaTeamId}
                    onChange={(e) => setFigmaTeamId(e.target.value)}
                    placeholder="e.g. 1234567890"
                    className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm"
                  />
                  <p className="mt-1 text-xs text-[var(--text-ter)]">
                    Found in your Figma team URL: figma.com/files/team/<strong>TEAM_ID</strong>/…
                    Required to browse team projects; individual file URLs work without it.
                  </p>
                </div>
                <div className="p-3 border border-[#1E1E1E]/10 bg-[#1E1E1E]/5">
                  <p className="text-sm text-[var(--text-sec)]">
                    <strong>What this enables:</strong>{' '}
                    Import Figma frames as prioritization items (like Miro sticky notes), export priorities back as
                    Figma comments, and link individual items to specific frames for full design traceability.
                  </p>
                </div>
              </div>
            ) : selectedConnectorType === 'intercom' ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                    Data Region
                  </label>
                  <select
                    value={intercomRegion}
                    onChange={(e) => setIntercomRegion(e.target.value)}
                    className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                  >
                    <option value="us">US (api.intercom.io)</option>
                    <option value="eu">EU (api.eu.intercom.io)</option>
                    <option value="au">AU (api.au.intercom.io)</option>
                  </select>
                  <p className="mt-1 text-xs text-[var(--text-ter)]">
                    Match your workspace's data-hosting region.
                  </p>
                </div>
                <div className="p-3 border border-[var(--slate)] bg-[var(--slate-dim)]">
                  <p className="text-sm text-[var(--text-sec)]">
                    You'll be redirected to <strong>Intercom</strong> to authorize read access to your
                    conversations (OAuth). No token to copy — we never see your password.
                  </p>
                </div>
                <div className="p-3 border border-[var(--green)]/70 bg-[var(--green-dim)]">
                  <p className="text-sm text-[var(--text-sec)]">
                    <strong className="text-[var(--text-pri)]">What this enables:</strong>{' '}
                    Agents and the app can read Intercom conversations as demand signals, and support
                    conversations can be ingested as candidate backlog items via webhook.
                  </p>
                </div>
              </div>
            ) : selectedConnectorType === 'zendesk' ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                    Subdomain
                  </label>
                  <input
                    type="text"
                    value={zendeskSubdomain}
                    onChange={(e) => setZendeskSubdomain(e.target.value)}
                    placeholder="acme (from acme.zendesk.com)"
                    className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm"
                  />
                  <p className="mt-1 text-xs text-[var(--text-ter)]">
                    The <strong>subdomain</strong> in <code>{'{subdomain}'}.zendesk.com</code>.
                  </p>
                </div>
                <div className="p-3 border border-[var(--slate)] bg-[var(--slate-dim)]">
                  <p className="text-sm text-[var(--text-sec)]">
                    You'll be redirected to <strong>Zendesk</strong> to authorize read access to your
                    tickets (OAuth 2.0 with token refresh). Legacy email/API-token auth is being retired
                    by Zendesk and is not used.
                  </p>
                </div>
                <div className="p-3 border border-[var(--green)]/70 bg-[var(--green-dim)]">
                  <p className="text-sm text-[var(--text-sec)]">
                    <strong className="text-[var(--text-pri)]">What this enables:</strong>{' '}
                    Agents and the app can read Zendesk Support tickets as demand signals, and tickets
                    can be ingested as candidate backlog items via webhook.
                  </p>
                </div>
              </div>
            ) : selectedConnectorType === 'hubspot' ? (
              <div className="space-y-4">
                <div className="p-3 border border-[var(--slate)] bg-[var(--slate-dim)]">
                  <p className="text-sm text-[var(--text-sec)]">
                    You'll be redirected to <strong>HubSpot</strong> to authorize read access to your
                    CRM deals (OAuth 2.0 with token refresh). No token to copy — your portal is
                    detected automatically after you approve.
                  </p>
                </div>
                <div className="p-3 border border-[var(--green)]/70 bg-[var(--green-dim)]">
                  <p className="text-sm text-[var(--text-sec)]">
                    <strong className="text-[var(--text-pri)]">What this enables:</strong>{' '}
                    Agents and the app can read HubSpot deals — stage, amount, and weighted pipeline
                    value — so what sales is promising informs what gets prioritized.
                  </p>
                </div>
              </div>
            ) : selectedConnectorType === 'salesforce' ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                    Login host <span className="font-normal text-[var(--text-ter)]">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={salesforceLoginUrl}
                    onChange={(e) => setSalesforceLoginUrl(e.target.value)}
                    placeholder="https://login.salesforce.com"
                    className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm"
                  />
                  <p className="mt-1 text-xs text-[var(--text-ter)]">
                    Leave blank for production. Use <code>https://test.salesforce.com</code> for a sandbox.
                    Your org's instance URL is detected automatically after you authorize.
                  </p>
                </div>
                <div className="p-3 border border-[var(--slate)] bg-[var(--slate-dim)]">
                  <p className="text-sm text-[var(--text-sec)]">
                    You'll be redirected to <strong>Salesforce</strong> to authorize read access to your
                    opportunities (OAuth 2.0 Web Server flow with token refresh). No token to copy — we
                    never see your password.
                  </p>
                </div>
                <div className="p-3 border border-[var(--green)]/70 bg-[var(--green-dim)]">
                  <p className="text-sm text-[var(--text-sec)]">
                    <strong className="text-[var(--text-pri)]">What this enables:</strong>{' '}
                    Agents and the app can read Salesforce opportunities as demand signals — revenue at
                    risk and customer feature requests — and opportunities can be ingested as candidate
                    backlog items via webhook.
                  </p>
                </div>
              </div>
            ) : selectedConnectorType === 'datadog' ? (
              <div className="space-y-4">
                {isConfigureMode && (connections.find(c => c.type === 'datadog')?.config as Record<string,string> | undefined)?.apiKey && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-[var(--green-dim)] border border-[var(--green)] text-sm text-[var(--green)] dark:text-[var(--text-ter)]">
                    <Check className="h-4 w-4 flex-shrink-0" />
                    <span>Credentials are saved. Leave any field blank to keep the current value.</span>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                    {isConfigureMode ? 'API Key (optional)' : 'API Key'}
                  </label>
                  <input
                    type="password"
                    value={datadogApiKey}
                    onChange={(e) => setDatadogApiKey(e.target.value)}
                    placeholder={isConfigureMode ? 'Leave blank to keep current key' : 'Enter your Datadog API key'}
                    className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                    {isConfigureMode ? 'Application Key (optional)' : 'Application Key'}
                  </label>
                  <input
                    type="password"
                    value={datadogAppKey}
                    onChange={(e) => setDatadogAppKey(e.target.value)}
                    placeholder={isConfigureMode ? 'Leave blank to keep current key' : 'Enter your Datadog application key'}
                    className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm"
                  />
                  <p className="mt-1 text-xs text-[var(--text-ter)]">
                    Create both under{' '}
                    <a href="https://app.datadoghq.com/organization-settings/api-keys" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">
                      Datadog → Organization Settings → API Keys
                    </a>
                    . The application key needs read scopes for SLOs, incidents, and monitors.
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                    Site <span className="text-[var(--text-ter)] font-normal">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={datadogSite}
                    onChange={(e) => setDatadogSite(e.target.value)}
                    placeholder="datadoghq.com"
                    className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm"
                  />
                  <p className="mt-1 text-xs text-[var(--text-ter)]">
                    Your Datadog region host — e.g. <strong>datadoghq.com</strong> (US1), <strong>datadoghq.eu</strong>,
                    or <strong>us5.datadoghq.com</strong>. Defaults to datadoghq.com.
                  </p>
                </div>
                <div className="p-3 border border-[#632CA6]/20 bg-[#632CA6]/5">
                  <p className="text-sm text-[var(--text-sec)]">
                    <strong>What this enables:</strong>{' '}
                    Attach live SLO status, open incident counts, and monitor alerting as evidence on reliability
                    items — so observability data informs what engineering prioritizes next.
                  </p>
                </div>
              </div>
            ) : selectedConnectorType === 'snowflake' ? (
              <div className="space-y-4">
                {isConfigureMode && (connections.find(c => c.type === 'snowflake')?.config as Record<string,string> | undefined)?.token && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-[var(--green-dim)] border border-[var(--green)] text-sm text-[var(--green)] dark:text-[var(--text-ter)]">
                    <Check className="h-4 w-4 flex-shrink-0" />
                    <span>Credentials are saved. Leave any field blank to keep the current value.</span>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                    Account Identifier
                  </label>
                  <input
                    type="text"
                    value={snowflakeAccount}
                    onChange={(e) => setSnowflakeAccount(e.target.value)}
                    placeholder="myorg-myaccount"
                    className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm"
                  />
                  <p className="mt-1 text-xs text-[var(--text-ter)]">
                    The host prefix before <strong>.snowflakecomputing.com</strong> (e.g. <strong>myorg-myaccount</strong>).
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Username</label>
                  <input
                    type="text"
                    value={snowflakeUsername}
                    onChange={(e) => setSnowflakeUsername(e.target.value)}
                    placeholder="Username the token was issued for"
                    className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                    {isConfigureMode ? 'Programmatic Access Token (optional)' : 'Programmatic Access Token'}
                  </label>
                  <input
                    type="password"
                    value={snowflakeToken}
                    onChange={(e) => setSnowflakeToken(e.target.value)}
                    placeholder={isConfigureMode ? 'Leave blank to keep current token' : 'PAT secret'}
                    className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm"
                  />
                  <p className="mt-1 text-xs text-[var(--text-ter)]">
                    Generate a Programmatic Access Token in{' '}
                    <a href="https://docs.snowflake.com/en/user-guide/programmatic-access-tokens" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">
                      Snowflake → Programmatic Access Tokens
                    </a>
                    . Used with the SQL API — no password required.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                      Warehouse <span className="text-[var(--text-ter)] font-normal">(optional)</span>
                    </label>
                    <input
                      type="text"
                      value={snowflakeWarehouse}
                      onChange={(e) => setSnowflakeWarehouse(e.target.value)}
                      placeholder="COMPUTE_WH"
                      className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                      Database <span className="text-[var(--text-ter)] font-normal">(optional)</span>
                    </label>
                    <input
                      type="text"
                      value={snowflakeDatabase}
                      onChange={(e) => setSnowflakeDatabase(e.target.value)}
                      placeholder="ANALYTICS"
                      className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                    Role <span className="text-[var(--text-ter)] font-normal">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={snowflakeRole}
                    onChange={(e) => setSnowflakeRole(e.target.value)}
                    placeholder="ANALYST"
                    className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm"
                  />
                </div>
                <div className="p-3 border border-[#29B5E8]/25 bg-[#29B5E8]/5">
                  <p className="text-sm text-[var(--text-sec)]">
                    <strong>What this enables:</strong>{' '}
                    Run read-only warehouse queries to enrich items with usage metrics, revenue attribution, and
                    custom business signals. Only SELECT / WITH / SHOW / DESCRIBE / EXPLAIN statements are permitted.
                  </p>
                </div>
              </div>
            ) : selectedConnectorType === 'google-calendar' ? (
              <div className="space-y-4">
                <div className="p-4 bg-[var(--slate-dim)] border border-[var(--slate)]">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0">
                      {ConnectorIcons['google-calendar']}
                    </div>
                    <div>
                      <h4 className="font-medium text-[var(--text-sec)]">Connect with Google</h4>
                      <p className="text-sm text-[var(--text-sec)] mt-1">
                        You'll be redirected to Google to authorize Peak Priorities to create calendar events on your behalf.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-[var(--text-sec)]">What this integration does:</h4>
                  <ul className="text-sm text-[var(--text-sec)] space-y-1">
                    <li className="flex items-start gap-2">
                      <Check className="h-4 w-4 text-[var(--green)] mt-0.5 flex-shrink-0" />
                      <span>Creates calendar events when participants are assigned to prioritization tasks</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="h-4 w-4 text-[var(--green)] mt-0.5 flex-shrink-0" />
                      <span>Sends calendar invites with meeting details and app links</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="h-4 w-4 text-[var(--green)] mt-0.5 flex-shrink-0" />
                      <span>Updates or cancels events when task details change</span>
                    </li>
                  </ul>
                </div>
                <div className="p-3 bg-[var(--mustard-dim)] border border-[var(--mustard)]">
                  <p className="text-sm text-[var(--mustard)]">
                    <strong>Note:</strong> This requires Google Cloud Console configuration. 
                    Contact your administrator if you see an error.
                  </p>
                </div>
              </div>
            ) : selectedConnectorType === 'zoom' ? (
              <div className="space-y-4">
                <div className="p-4 bg-[var(--slate-dim)] border border-[var(--slate)]">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0">
                      {ConnectorIcons.zoom}
                    </div>
                    <div>
                      <h4 className="font-medium text-[var(--text-sec)]">Connect with Zoom</h4>
                      <p className="text-sm text-[var(--text-sec)] mt-1">
                        You'll be redirected to Zoom to authorize Peak Priorities to create meetings on your behalf.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-[var(--text-sec)]">What this integration does:</h4>
                  <ul className="text-sm text-[var(--text-sec)] space-y-1">
                    <li className="flex items-start gap-2">
                      <Check className="h-4 w-4 text-[var(--green)] mt-0.5 flex-shrink-0" />
                      <span>Creates Zoom meetings when participants are assigned to prioritization sessions</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="h-4 w-4 text-[var(--green)] mt-0.5 flex-shrink-0" />
                      <span>Sends join links directly to session attendees</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="h-4 w-4 text-[var(--green)] mt-0.5 flex-shrink-0" />
                      <span>Cancels meetings automatically when sessions are removed</span>
                    </li>
                  </ul>
                </div>
                <div className="p-3 bg-[var(--mustard-dim)] border border-[var(--mustard)]">
                  <p className="text-sm text-[var(--mustard)]">
                    <strong>Note:</strong> This requires a Zoom app configured in the Zoom Marketplace. 
                    Contact your administrator if you see an error.
                  </p>
                </div>
              </div>
            ) : selectedConnectorType === 'google-meet' ? (
              <div className="space-y-4">
                <div className="p-4 bg-[var(--slate-dim)] border border-[var(--slate)]">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0">
                      {ConnectorIcons['google-meet']}
                    </div>
                    <div>
                      <h4 className="font-medium text-[var(--text-sec)]">Connect with Google Meet</h4>
                      <p className="text-sm text-[var(--text-sec)] mt-1">
                        You'll be redirected to Google to authorize Peak Priorities to create Meet video links via Google Calendar.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-[var(--text-sec)]">What this integration does:</h4>
                  <ul className="text-sm text-[var(--text-sec)] space-y-1">
                    <li className="flex items-start gap-2">
                      <Check className="h-4 w-4 text-[var(--green)] mt-0.5 flex-shrink-0" />
                      <span>Generates Google Meet video links for prioritization sessions</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="h-4 w-4 text-[var(--green)] mt-0.5 flex-shrink-0" />
                      <span>Creates calendar events with embedded Meet links for all attendees</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="h-4 w-4 text-[var(--green)] mt-0.5 flex-shrink-0" />
                      <span>Updates or removes events automatically when session details change</span>
                    </li>
                  </ul>
                </div>
                <div className="p-3 bg-[var(--mustard-dim)] border border-[var(--mustard)]">
                  <p className="text-sm text-[var(--mustard)]">
                    <strong>Note:</strong> This uses the Google Calendar API with Meet scope.
                    Requires Google Cloud Console configuration. Contact your administrator if you see an error.
                  </p>
                </div>
              </div>
            ) : (selectedConnectorType === 'dropbox' || selectedConnectorType === 'google-docs') ? (
              <div className="space-y-4">
                <div className="p-4 bg-[var(--slate-dim)] border border-[var(--slate)]">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0">
                      {ConnectorIcons[selectedConnectorType]}
                    </div>
                    <div>
                      <h4 className="font-medium text-[var(--text-sec)]">
                        {selectedConnectorType === 'dropbox' ? 'Connect with Dropbox' : 'Connect with Google'}
                      </h4>
                      <p className="text-sm text-[var(--text-sec)] mt-1">
                        You'll be redirected to {selectedConnectorType === 'dropbox' ? 'Dropbox' : 'Google'} to authorize
                        Peak Priorities to read your {selectedConnectorType === 'dropbox' ? 'files' : 'documents'} (read-only).
                      </p>
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-[var(--text-sec)]">What this connection does:</h4>
                  <ul className="text-sm text-[var(--text-sec)] space-y-1">
                    <li className="flex items-start gap-2">
                      <Check className="h-4 w-4 text-[var(--green)] mt-0.5 flex-shrink-0" />
                      <span>Makes your {selectedConnectorType === 'dropbox' ? 'Dropbox files' : 'Google Docs'} available as a content data source</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="h-4 w-4 text-[var(--green)] mt-0.5 flex-shrink-0" />
                      <span>Lets AI agents list, search, and read that content via the platform API</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="h-4 w-4 text-[var(--green)] mt-0.5 flex-shrink-0" />
                      <span>Read-only — Peak Priorities never modifies or deletes your {selectedConnectorType === 'dropbox' ? 'files' : 'documents'}</span>
                    </li>
                  </ul>
                </div>
                <div className="p-3 bg-[var(--mustard-dim)] border border-[var(--mustard)]">
                  <p className="text-sm text-[var(--mustard)]">
                    <strong>Note:</strong> Requires {selectedConnectorType === 'dropbox' ? 'a Dropbox app' : 'Google Cloud Console'} configuration.
                    Contact your administrator if you see an error.
                  </p>
                </div>
              </div>
            ) : null}

            <div className="mt-6 flex gap-3">
              <button
                onClick={() => {
                  setShowConnectModal(false);
                  resetForm();
                }}
                className="flex-1 px-4 py-2 border border-[var(--rule)] hover:bg-[var(--surface)] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveConnection}
                disabled={isLoading || (!isConfigureMode && (
                  selectedConnectorType === 'miro' ? !miroToken : 
                  selectedConnectorType === 'jira' ? (!jiraSiteUrl || !jiraEmail || !jiraToken) :
                  selectedConnectorType === 'slack' ? (isConfigureMode && !slackNotifyChannelId) :
                  selectedConnectorType === 'notion' ? false :
                  selectedConnectorType === 'linear' ? false :
                  selectedConnectorType === 'google-calendar' ? false :
                  selectedConnectorType === 'zoom' ? false :
                  selectedConnectorType === 'google-meet' ? false :
                  selectedConnectorType === 'dropbox' ? false :
                  selectedConnectorType === 'google-docs' ? false :
                  selectedConnectorType === 'figma' ? !figmaAccessToken :
                  selectedConnectorType === 'quantive' ? (!quantiveApiKey || !quantiveAccountId) :
                  selectedConnectorType === 'granola' ? !granolaApiKey :
                  selectedConnectorType === 'raycast' ? !raycastApiKey :
                  selectedConnectorType === 'mcp' ? !mcpApiKey :
                  selectedConnectorType === 'intercom' ? false :
                  selectedConnectorType === 'zendesk' ? !zendeskSubdomain :
                  selectedConnectorType === 'hubspot' ? false :
                  selectedConnectorType === 'salesforce' ? false :
                  selectedConnectorType === 'datadog' ? (!datadogApiKey || !datadogAppKey) :
                  selectedConnectorType === 'snowflake' ? (!snowflakeAccount || !snowflakeUsername || !snowflakeToken) :
                  true
                ))}
                className="flex-1 px-4 py-2 bg-primary-600 text-white hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {(selectedConnectorType === 'google-calendar' || selectedConnectorType === 'zoom' || selectedConnectorType === 'google-meet' || (selectedConnectorType === 'slack' && !isConfigureMode) || selectedConnectorType === 'intercom' || selectedConnectorType === 'zendesk' || selectedConnectorType === 'hubspot' || selectedConnectorType === 'dropbox' || selectedConnectorType === 'google-docs' || selectedConnectorType === 'salesforce' || (selectedConnectorType === 'linear' && !linearApiKey)) ? 'Redirecting...' : isConfigureMode ? 'Saving...' : 'Connecting...'}
                  </>
                ) : (
                  selectedConnectorType === 'slack' ? (isConfigureMode ? 'Save workspace channel' : 'Connect with Slack')
                    : (selectedConnectorType === 'linear' && !isConfigureMode && !linearApiKey) ? 'Connect with Linear'
                    : selectedConnectorType === 'google-calendar' ? (isConfigureMode ? 'Reconnect with Google' : 'Connect with Google')
                    : selectedConnectorType === 'zoom' ? (isConfigureMode ? 'Reconnect with Zoom' : 'Connect with Zoom')
                    : selectedConnectorType === 'google-meet' ? (isConfigureMode ? 'Reconnect with Google' : 'Connect with Google')
                    : selectedConnectorType === 'dropbox' ? (isConfigureMode ? 'Reconnect with Dropbox' : 'Connect with Dropbox')
                    : selectedConnectorType === 'google-docs' ? (isConfigureMode ? 'Reconnect with Google' : 'Connect with Google')
                    : isConfigureMode ? 'Save Changes'
                    : 'Connect'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
